import React from 'react';
import axios from 'axios';
import {
  ArrowLeft, Phone, MapPin, Cake, Users as UsersIcon,
  AlertTriangle, Pill, Tag, Printer, MessageCircle, Mic,
  Camera, Edit3, Trash2, Plus, FileText, Receipt,
  Upload, ChevronDown, Stethoscope, Star, ClipboardList,
  Clock, X, Activity, CreditCard, FlaskConical,
} from 'lucide-react';
import { theme, inputStyle, labelStyle } from '../theme';
import PrescriptionForm from '../components/PrescriptionForm';
import PatientVisitView from '../components/PatientVisitView';
import TreatmentSection from '../components/TreatmentSection';
import NotesSection from '../components/NotesSection';
import PaymentSection from '../components/PaymentSection';
import Odontogram from '../components/clinical/Odontogram';
import DocumentVault from '../components/DocumentVault';
import QuickFeedback from '../components/QuickFeedback';
import PatientCommsPane from '../components/PatientCommsPane';
import PatientCallHistory from '../components/PatientCallHistory';
import FaceRegister from '../components/FaceRegister';
import SmartRecordModal from '../components/SmartRecordModal';
import ClinicalPage from './ClinicalPage';
import VitalSignsPage from './VitalSignsPage';
import ImagingHubPage from './ImagingHubPage';
import { useClinic } from '../context/ClinicContext';

/**
 * PatientProfile — redesigned "world's best dental patient profile".
 *
 * Layout:
 *   Sticky LEFT sidebar (snapshot + safety alerts + quick actions)
 *   RIGHT content area (tab-driven, swaps inline, no modal overlays)
 *
 * Tabs: Overview · Visits · Rx · Bills · Chart · Documents · Notes · Feedback · Forms
 */
const PatientProfile = ({ app }) => {
  const { clinic } = useClinic();
  const [showFaceReg, setShowFaceReg] = React.useState(false);
  const [paymentOpen, setPaymentOpen] = React.useState(false);
  const [fabOpen, setFabOpen] = React.useState(false);
  const [newVisitTrigger, setNewVisitTrigger] = React.useState(0);
  // Counter for auto-opening DocumentVault's upload modal — bumped when
  // the action drawer's "Upload doc" is clicked.
  const [uploadDocTrigger, setUploadDocTrigger] = React.useState(0);
  const [showSmartRecord, setShowSmartRecord] = React.useState(false);
  const [editMode, setEditMode] = React.useState(false);
  const [editForm, setEditForm] = React.useState({});
  const [ptOutstanding, setPtOutstanding] = React.useState(null);
  const [clinicalSub, setClinicalSub] = React.useState('imaging'); // Clinical tab sub-tab (Vitals moved to sidebar)
  const [sidebarHover, setSidebarHover] = React.useState(false); // sidebar collapse-on-hover
  const {
    API_BASE, UPLOAD_BASE,
    patientsList, searchTerm, setSearchTerm,
    selectedPatient, setSelectedPatient, resetViews, fetchAllPatients,
    pastTreatments, pastBills, pastPrescriptions,
    patientReports, fetchPastPrescriptions,
    patientTab, setPatientTab,
    isWritingPrescription, setIsWritingPrescription,
    setIsWritingBill,
    chiefComplaintList, findingsList, investigationList, diagnosisList,
    billForm, setBillForm, currentBillItem, setCurrentBillItem, billTotal,
    handleAddBillItem, handleSaveBill,
    ratePlans,
    treatmentTypes, instructionList, medicineLibrary,
    reportTitle, setReportTitle, reportFile, setReportFile, handleFileUpload,
    printPatientCard, whatsappPatientSummary, whatsappTreatmentDone, whatsappPaymentReceived, whatsappTreatmentPlan,
    medicalList, dentalList, medicinesList, allergiesList, groupsList, tagsList,
    setShowRegForm,
  } = app;
  const [patientConsents, setPatientConsents] = React.useState([]);
  const [opdStatus, setOpdStatus] = React.useState(null);

  // Fetch outstanding balance + consents + OPD status when patient is selected
  React.useEffect(() => {
    if (selectedPatient?._id) {
      axios.get(`${API_BASE}/payments/patient/${selectedPatient._id}/outstanding`)
        .then(r => setPtOutstanding(r.data))
        .catch(() => setPtOutstanding(null));
      axios.get(`${API_BASE}/treatments/consents/${selectedPatient._id}`)
        .then(r => setPatientConsents(r.data))
        .catch(() => setPatientConsents([]));
      axios.get(`${API_BASE}/patients/${selectedPatient._id}/opd-status`)
        .then(r => setOpdStatus(r.data))
        .catch(() => setOpdStatus(null));
    } else {
      setPtOutstanding(null);
      setPatientConsents([]);
      setOpdStatus(null);
    }
  }, [selectedPatient, API_BASE]);

  // Charge OPD fee from the profile (manual trigger when expired)
  const handleChargeOpdFee = async () => {
    if (!window.confirm(`Charge ₹${opdStatus?.fee || 0} OPD fee now?`)) return;
    try {
      const res = await axios.post(`${API_BASE}/patients/${selectedPatient._id}/charge-opd-fee`, { mode: 'cash' });
      alert(`OPD fee charged. Valid till ${new Date(res.data.validTill).toLocaleDateString('en-IN')}`);
      const s = await axios.get(`${API_BASE}/patients/${selectedPatient._id}/opd-status`);
      setOpdStatus(s.data);
    } catch (e) {
      alert('Charge failed: ' + (e.response?.data?.error || e.message));
    }
  };

  const startEdit = () => {
    setEditForm({
      title: selectedPatient.title || '',
      name: selectedPatient.name || '',
      phone: selectedPatient.phone || '',
      gender: selectedPatient.gender || '',
      dob: selectedPatient.dob ? new Date(selectedPatient.dob).toISOString().split('T')[0] : '',
      age: selectedPatient.age || '',
      address: selectedPatient.address || '',
      occupation: selectedPatient.occupation || '',
      referredBy: selectedPatient.referredBy || '',
      aadhaar: selectedPatient.aadhaar || '',
      notes: selectedPatient.notes || '',
      familyId: selectedPatient.familyId || '',
      familyRelation: selectedPatient.familyRelation || '',
      insurance: selectedPatient.insurance || { companyName: '', subType: '' },
      medicalHistory: selectedPatient.medicalHistory || [],
      dentalHistory: selectedPatient.dentalHistory || [],
      currentMedicines: selectedPatient.currentMedicines || [],
      allergies: selectedPatient.allergies || [],
      groups: selectedPatient.groups || [],
      tags: selectedPatient.tags || [],
    });
    setEditMode(true);
  };

  const saveEdit = async () => {
    try {
      const res = await axios.put(`${API_BASE}/patients/${selectedPatient._id}`, {
        ...editForm,
        familyId: editForm.familyId,
        familyRelation: editForm.familyRelation,
        insurance: JSON.stringify(editForm.insurance),
        medicalHistory: JSON.stringify(editForm.medicalHistory),
        dentalHistory: JSON.stringify(editForm.dentalHistory),
        currentMedicines: JSON.stringify(editForm.currentMedicines),
        allergies: JSON.stringify(editForm.allergies),
        groups: JSON.stringify(editForm.groups),
        tags: JSON.stringify(editForm.tags),
      });
      setSelectedPatient(res.data);
      setEditMode(false);
      fetchAllPatients();
      alert('Patient updated');
    } catch (e) { alert('Update failed'); }
  };

  // Print mode state — letterhead vs blank paper
  const [printModeModal, setPrintModeModal] = React.useState(null); // null = hidden, else callback fn

  // Show modal, resolve with 'plain' or 'letterhead'
  const askPrintMode = () => {
    return new Promise(resolve => {
      setPrintModeModal(() => resolve);
    });
  };

  // Print prescription
  const handlePrintRx = async (rx) => {
    const printMode = await askPrintMode();
    const ptName = (selectedPatient.title?selectedPatient.title+' ':'')+selectedPatient.name;
    const meds = rx.medicines || [];
    const isLetterhead = printMode === 'letterhead';
    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>Prescription</title>
    <style>@page{size:A4;margin:15mm}*{box-sizing:border-box;margin:0;padding:0}body{font-family:Arial,sans-serif;color:#222;font-size:12px;line-height:1.5}
    .rx{max-width:700px;margin:0 auto;padding:20px}.header{text-align:center;border-bottom:2px solid #333;padding-bottom:10px;margin-bottom:14px}
    .clinic{font-size:18px;font-weight:bold}.rx-symbol{font-size:24px;font-weight:bold;color:#006494;margin:10px 0}
    table{width:100%;border-collapse:collapse;margin:10px 0}th,td{border:1px solid #ccc;padding:6px 8px;text-align:left;font-size:11px}
    th{background:#f5f5f5;font-size:10px;text-transform:uppercase}.sign{display:flex;justify-content:space-between;margin-top:40px}
    .sign-line{border-top:1px solid #333;margin-top:40px;padding-top:4px;font-size:9px;text-align:center;width:150px}
    @media print{body{-webkit-print-color-adjust:exact}}</style></head><body>
    <div class="rx">${isLetterhead ? '<div style="height:80px"></div>' : `<div class="header"><div class="clinic">${(clinic?.clinicName || 'Your Clinic').toUpperCase()}</div>
    <div style="font-size:9px;color:#555">${[clinic?.clinicAddress, clinic?.clinicPhone && 'Ph: ' + clinic.clinicPhone].filter(Boolean).join(' | ')}</div></div>`}
    <div style="display:flex;justify-content:space-between;margin-bottom:12px;font-size:11px">
    <div><strong>Patient:</strong> ${ptName}<br><strong>UHID:</strong> ${selectedPatient.uhid||''}<br><strong>Phone:</strong> ${selectedPatient.phone||''}</div>
    <div style="text-align:right"><strong>Date:</strong> ${new Date(rx.date).toLocaleDateString('en-IN',{day:'2-digit',month:'long',year:'numeric'})}<br>
    <strong>Age/Gender:</strong> ${selectedPatient.age||''} yrs / ${selectedPatient.gender||''}</div></div>
    <div class="rx-symbol">℞</div>
    ${rx.chiefComplaint?`<p><strong>Chief Complaint:</strong> ${rx.chiefComplaint}</p>`:''}
    ${rx.findings?`<p><strong>Findings:</strong> ${rx.findings}</p>`:''}
    ${rx.investigation?`<p><strong>Investigation:</strong> ${rx.investigation}</p>`:''}
    ${rx.diagnosis?`<p style="margin-bottom:10px"><strong>Diagnosis:</strong> ${rx.diagnosis}</p>`:''}
    ${rx.clinicalNotes?.length>0?`<p><strong>Teeth:</strong> ${rx.clinicalNotes.map(cn=>'#'+cn.toothNumber+(cn.surfaces?.length?' ['+cn.surfaces.join(',')+']':'')).join(', ')}</p>`:''}
    ${meds.length>0?`<table><thead><tr><th>#</th><th>Medicine</th><th>Dosage</th><th>Duration</th><th>Instructions</th></tr></thead>
    <tbody>${meds.map((m,i)=>`<tr><td>${i+1}</td><td><strong>${m.name}</strong></td><td>${m.dosage||'-'}</td><td>${m.duration||'-'}</td><td>${m.instructions||'-'}</td></tr>`).join('')}</tbody></table>`:''}
    ${rx.notes?`<p style="margin-top:10px;font-style:italic"><strong>Notes:</strong> ${rx.notes}</p>`:''}
    <div class="sign"><div class="sign-line">Patient Signature</div><div class="sign-line">Doctor's Signature & Stamp</div></div>
    </div><script>window.onload=function(){window.print();window.close();}</script></body></html>`);
    w.document.close();
  };

  // Print full case sheet
  const handlePrintCaseSheet = async () => {
    const printMode = await askPrintMode();
    const pt = selectedPatient;
    const ptName = (pt.title?pt.title+' ':'')+pt.name;
    const isLetterhead = printMode === 'letterhead';
    const todayFmt = new Date().toLocaleDateString('en-IN',{day:'2-digit',month:'2-digit',year:'numeric'});
    const timeFmt = new Date().toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit',hour12:true});
    const regDateFmt = pt.patientSince ? new Date(pt.patientSince).toLocaleDateString('en-IN',{day:'2-digit',month:'2-digit',year:'numeric'}) : todayFmt;
    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>Case Sheet - ${ptName}</title>
    <style>
      @page { size: A4; margin: 8mm; }
      * { box-sizing: border-box; margin: 0; padding: 0; }
      body { font-family: Arial, sans-serif; color: #000; font-size: 12px; line-height: 1.4; }
      .page { max-width: 780px; margin: 0 auto; border: 2px solid #000; }

      /* Hospital Header */
      .hospital-header {
        background: linear-gradient(135deg, #00b5b8 0%, #006494 100%);
        color: white; padding: 14px 20px; text-align: center;
        border-bottom: 3px solid #004466;
      }
      .hospital-name { font-size: 24px; font-weight: 900; letter-spacing: 2px; text-transform: uppercase; }
      .hospital-tagline { font-size: 11px; font-weight: 600; margin-top: 2px; letter-spacing: 1px; opacity: 0.9; }
      .hospital-address { font-size: 10px; margin-top: 6px; opacity: 0.85; }
      .hospital-phone { font-size: 11px; font-weight: 700; margin-top: 4px; }

      /* Patient Info Table */
      .info-table { width: 100%; border-collapse: collapse; }
      .info-table td { border: 1px solid #333; padding: 5px 10px; font-size: 11px; vertical-align: top; }
      .info-label { font-weight: 700; width: 130px; background: #f0f7f7; color: #004466; white-space: nowrap; }

      /* OPD Box */
      .opd-box { text-align: center; border: 2px solid #000; padding: 8px; margin: 0; }
      .opd-label { font-size: 10px; font-weight: 700; color: #666; }
      .opd-value { font-size: 22px; font-weight: 900; color: #c0392b; }

      /* Sections */
      .section-title { font-size: 13px; font-weight: 800; background: #004466; color: white; padding: 5px 12px; margin: 0; text-transform: uppercase; letter-spacing: 1px; }
      .section-body { padding: 8px 12px; border: 1px solid #333; border-top: none; }

      /* Treatment/Bill tables */
      .data-table { width: 100%; border-collapse: collapse; margin: 4px 0; }
      .data-table th { background: #e8f4f8; border: 1px solid #999; padding: 5px 8px; font-size: 10px; text-transform: uppercase; font-weight: 700; }
      .data-table td { border: 1px solid #ccc; padding: 4px 8px; font-size: 10px; }

      .rx-card { border: 1px solid #ddd; padding: 8px; margin-bottom: 6px; background: #fafafa; }
      .rx-date { font-weight: 700; color: #004466; margin-bottom: 3px; }

      .footer { text-align: center; padding: 10px; font-size: 9px; color: #888; border-top: 1px solid #ddd; }
      .sign-area { display: flex; justify-content: space-between; padding: 30px 20px 10px; }
      .sign-line { border-top: 1px solid #333; padding-top: 4px; font-size: 9px; text-align: center; width: 160px; }

      .barcode-area { text-align: center; padding: 6px 0; }
      .barcode-area svg { max-width: 220px; }
      @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
    </style>
    <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js"><\/script>
    </head><body>
    <div class="page">

      <!-- Hospital Header -->
      ${isLetterhead ? '<div style="height:90px"></div>' : `<div class="hospital-header">
        <div class="hospital-name">${(clinic?.clinicName || 'Your Clinic').toUpperCase()}</div>
        ${clinic?.headerNote ? `<div class="hospital-tagline">${clinic.headerNote}</div>` : '<div class="hospital-tagline">Complete Dental Care &amp; Research Centre</div>'}
        ${clinic?.clinicAddress ? `<div class="hospital-address">${clinic.clinicAddress}</div>` : ''}
        <div class="hospital-phone">${[clinic?.clinicPhone && 'Ph: ' + clinic.clinicPhone, clinic?.clinicEmail && 'e-mail: ' + clinic.clinicEmail].filter(Boolean).join(' &nbsp; | &nbsp; ')}</div>
      </div>`}

      <!-- Barcode Row (below header, above patient table) -->
      <div style="display:flex; justify-content:space-between; align-items:center; padding:6px 12px; border:1px solid #333; border-top:none;">
        <div style="font-size:10px; color:#666;"><strong>Case Sheet</strong> &nbsp;|&nbsp; ${todayFmt} ${timeFmt}</div>
        <svg id="uhidBarcode"></svg>
      </div>

      <!-- Patient Details Table -->
      <table class="info-table">
        <tr>
          <td class="info-label">UHID No.</td>
          <td><strong>${pt.uhid||''}</strong></td>
          <td class="info-label">Date</td>
          <td>${todayFmt} &nbsp; ${timeFmt}</td>
        </tr>
        <tr>
          <td class="info-label">Pt. Name</td>
          <td><strong style="font-size:13px">${ptName}</strong></td>
          <td class="info-label">Age / Sex</td>
          <td>${pt.age||'-'} yrs / ${pt.gender||'-'}</td>
        </tr>
        <tr>
          <td class="info-label">S/O, D/O, W/O</td>
          <td>${pt.referredBy||'-'}</td>
          <td class="info-label">Mobile No.</td>
          <td><strong>${pt.phone||'-'}</strong></td>
        </tr>
        <tr>
          <td class="info-label">Address</td>
          <td>${pt.address||'-'}</td>
          <td class="info-label">Occupation</td>
          <td>${pt.occupation||'-'}</td>
        </tr>
        <tr>
          <td class="info-label">Aadhaar No.</td>
          <td>${pt.aadhaar||'-'}</td>
          <td class="info-label">Reg. Date</td>
          <td>${regDateFmt}</td>
        </tr>
        <tr>
          <td class="info-label">DOB</td>
          <td>${pt.dob?new Date(pt.dob).toLocaleDateString('en-IN',{day:'2-digit',month:'2-digit',year:'numeric'}):'-'}</td>
          <td class="info-label">Department</td>
          <td>DENTAL</td>
        </tr>
        ${pt.insurance?.companyName?`<tr>
          <td class="info-label">Insurance</td>
          <td colspan="3">${pt.insurance.companyName} ${pt.insurance.subType?'('+pt.insurance.subType+')':''}</td>
        </tr>`:''}
      </table>

      <!-- OPD Number Box -->
      <table class="info-table" style="margin-top:-1px">
        <tr>
          <td class="opd-box" style="width:50%">
            <div class="opd-label">OPD NO.</div>
            <div class="opd-value">${pt.uhid||''}</div>
          </td>
          <td class="opd-box" style="width:25%">
            <div class="opd-label">Consultant</div>
            <div style="font-size:13px;font-weight:700;color:#004466;margin-top:2px">Dr. Sumit Kumar</div>
          </td>
          <td class="opd-box" style="width:25%">
            <div class="opd-label">Panel</div>
            <div style="font-size:12px;font-weight:600;margin-top:2px">GENERAL</div>
          </td>
        </tr>
      </table>

      <!-- Medical History -->
      <div class="section-title">Medical History</div>
      <div class="section-body">${(pt.medicalHistory||[]).join(', ')||'None recorded'}</div>

      <!-- Dental History -->
      <div class="section-title">Dental History</div>
      <div class="section-body">${(pt.dentalHistory||[]).join(', ')||'None recorded'}</div>

      <!-- Current Medicines -->
      <div class="section-title">Current Medicines</div>
      <div class="section-body">${(pt.currentMedicines||[]).join(', ')||'None'}</div>

      <!-- Allergies -->
      <div class="section-title">Allergies</div>
      <div class="section-body" style="color:#c0392b;font-weight:600">${(pt.allergies||[]).join(', ')||'No known allergies'}</div>

      <!-- Prescriptions -->
      <div class="section-title">Prescriptions (${pastPrescriptions.length})</div>
      <div class="section-body">
      ${pastPrescriptions.length===0?'<p style="color:#999">None</p>':pastPrescriptions.map(rx=>`<div class="rx-card">
        <div class="rx-date">${new Date(rx.date).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'})}</div>
        ${rx.chiefComplaint?`<div><strong>Chief Complaint:</strong> ${rx.chiefComplaint}</div>`:''}
        ${rx.findings?`<div><strong>Findings:</strong> ${rx.findings}</div>`:''}
        ${rx.investigation?`<div><strong>Investigation:</strong> ${rx.investigation}</div>`:''}
        ${rx.diagnosis?`<div><strong>Diagnosis:</strong> ${rx.diagnosis}</div>`:''}
        ${(rx.medicines||[]).length>0?`<div><strong>Medicines:</strong> ${rx.medicines.map(m=>m.name+(m.dosage?' ('+m.dosage+')':'')).join(', ')}</div>`:''}
      </div>`).join('')}
      </div>

      <!-- Treatments -->
      <div class="section-title">Treatments (${pastTreatments.length})</div>
      <div class="section-body">
      ${pastTreatments.length===0?'<p style="color:#999">None</p>':`<table class="data-table">
        <thead><tr><th>Treatment</th><th>Tooth #</th><th>Status</th><th>Amount</th><th>Date</th></tr></thead>
        <tbody>${pastTreatments.map(t=>`<tr>
          <td><strong>${t.treatmentName}</strong></td><td>${t.toothNumber||'-'}</td>
          <td style="color:${t.status==='done'?'#27ae60':'#e67e22'};font-weight:700">${t.status.toUpperCase()}</td>
          <td>₹${(t.totalAmount||0).toLocaleString('en-IN')}</td>
          <td>${t.doneDate?new Date(t.doneDate).toLocaleDateString('en-IN'):new Date(t.plannedDate||t.createdAt).toLocaleDateString('en-IN')}</td>
        </tr>`).join('')}</tbody></table>`}
      </div>

      <!-- Bills -->
      <div class="section-title">Bills (${pastBills.length})</div>
      <div class="section-body">
      ${pastBills.length===0?'<p style="color:#999">None</p>':`<table class="data-table">
        <thead><tr><th>Date</th><th>Invoice #</th><th>Items</th><th>Amount</th></tr></thead>
        <tbody>${pastBills.map(b=>`<tr>
          <td>${new Date(b.date).toLocaleDateString('en-IN')}</td><td><strong>${b.invoiceNo||'-'}</strong></td>
          <td>${(b.items||[]).map(i=>i.description).join(', ')}</td><td>₹${(b.totalAmount||0).toLocaleString('en-IN')}</td>
        </tr>`).join('')}</tbody></table>`}
      </div>

      <!-- Signature -->
      <div class="sign-area">
        <div class="sign-line">Patient / Guardian Signature</div>
        <div class="sign-line">Doctor's Signature & Stamp</div>
      </div>

      <div class="footer">Printed on ${todayFmt} — ${clinic?.clinicName || 'Your Clinic'}${clinic?.clinicPhone ? ' | Ph: ' + clinic.clinicPhone : ''}</div>
    </div>
    <script>
      window.onload=function(){
        try {
          JsBarcode("#uhidBarcode", "${pt.uhid||'0000'}", {
            format: "CODE128",
            width: 2, height: 50,
            displayValue: true,
            fontSize: 12, fontOptions: "bold",
            margin: 2,
            background: "white",
          });
        } catch(e) {}
        setTimeout(function(){ window.print(); window.close(); }, 300);
      };
    </script></body></html>`);
    w.document.close();
  };

  // Print discharge summary
  // AI-generated post-op instructions based on treatment type. Content mirrors the bilingual post-op PDF (Apr 2026).
  // Order matters — specific checks (wisdom, pediatric, gum surgery) must precede generic ones (extraction, crown).
  const getPostOpInstructions = (treatmentName) => {
    const tn = (treatmentName||'').toLowerCase();
    if (tn.includes('wisdom') || tn.includes('impaction') || tn.includes('impacted') || (tn.includes('surgical') && tn.includes('extract'))) return '1. बाकी दिन आराम करें, 24-48 घंटे गतिविधि सीमित रखें। 2. 45 मिनट तक गॉज़ पर मज़बूती से काटें, ताज़ा खून बहने पर बदलें। 3. पहले 48 घंटे आइस पैक लगाएं (20 मिनट लगाएं, 20 मिनट हटाएं) — काफी सूजन सामान्य है। 4. सभी दवाइयां (एंटीबायोटिक + तेज़ दर्द निवारक) बिल्कुल निर्देशानुसार लें। 5. सर्जरी के 24 घंटे बाद से दिन में 4-5 बार गर्म नमक पानी से गरारे करें। 6. पहले 24 घंटे थूकें नहीं, स्ट्रॉ न उपयोग करें, ज़ोर से कुल्ला न करें — ड्राई सॉकेट का खतरा। 7. कम से कम 1 सप्ताह धूम्रपान न करें — ड्राई सॉकेट का #1 कारण। 8. 1 सप्ताह तक कड़ा, गर्म या तीखा खाना न खाएं। 9. 48-72 घंटे ज़ोरदार व्यायाम न करें। 10. तेज़ दर्द, मुंह न खुलना, बुखार या 24 घंटे बाद भी सुन्नपन हो तो तुरंत संपर्क करें।';
    if (tn.includes('pulpotomy') || tn.includes('pulp capping') || tn.includes('pediatric') || tn.includes('space maintainer') || tn.includes('stainless steel crown')) return '1. सुन्न होंठ/गाल काटने से बच्चे को रोकें — 2-3 घंटे निगरानी रखें। 2. पहले कुछ घंटों में नरम, ठंडा खाना दें (आइसक्रीम, दही, मसला हुआ खाना)। 3. एनेस्थीसिया पूरी तरह उतरने तक गर्म खाना या पेय न दें। 4. केवल निर्धारित दवा (पेरासिटामॉल सिरप) उम्र के अनुसार दें। 5. अगले दिन से धीरे-धीरे दांत ब्रश करना शुरू करें, उस क्षेत्र में ज़बरदस्ती ब्रश न करें। 6. बच्चे को आश्वस्त करें — सकारात्मक प्रोत्साहन भविष्य के डेंटल विजिट के लिए मदद करता है। 7. चेहरे/आंख के पास सूजन, 100.4°F से अधिक बुखार, मुंह न खुलना या होंठ/गाल काटने की चोट हो तो तुरंत संपर्क करें।';
    if (tn.includes('flap') || tn.includes('gingivectomy') || tn.includes('bone graft') || tn.includes('sinus lift') || tn.includes('apicoectomy') || tn.includes('frenectomy') || tn.includes('curettage') || tn.includes('gum surgery')) return '1. सभी दवाइयां कड़ाई से लें — एंटीबायोटिक, दर्द निवारक और क्लोरहेक्सिडीन रिंस। 2. पहले 24 घंटे आइस पैक लगाएं (20 मिनट लगाएं, 20 मिनट हटाएं)। 3. सर्जिकल क्षेत्र को न छुएं — 1 सप्ताह तक वहां ब्रश नहीं, केवल हल्के से कुल्ला। 4. कम से कम 2 सप्ताह तक बहुत नरम खाना खाएं (दलिया, दाल, दही)। 5. कम से कम 2 सप्ताह धूम्रपान न करें — उपचार को गंभीर रूप से प्रभावित करता है। 6. 48 घंटे ज़ोर से न थूकें या स्ट्रॉ न उपयोग करें। 7. निर्धारित तारीख (आमतौर पर दिन 7-10) पर टांके हटवाने आएं। 8. दबाव से भी खून न रुके, टांके टूटें, सूजन गर्दन/आंख तक फैले या 101°F से अधिक बुखार हो तो तुरंत संपर्क करें।';
    if (tn.includes('implant')) return '1. बिना खुराक छोड़े पूरे कोर्स की एंटीबायोटिक लें। 2. 24-48 घंटे आइस पैक लगाएं (20 मिनट लगाएं, 20 मिनट हटाएं)। 3. इम्प्लांट के हड्डी से जुड़ने तक कम से कम 6 सप्ताह केवल नरम खाना खाएं। 4. दी गई क्लोरहेक्सिडीन रिंस या बहुत धीरे ब्रश से क्षेत्र साफ रखें। 5. धूम्रपान बिल्कुल न करें — इम्प्लांट फेलियर का सबसे बड़ा कारण। 6. डॉक्टर की अनुमति (3-6 महीने) से पहले इम्प्लांट पर कड़ा खाना न डालें। 7. ब्लड शुगर नियंत्रण न छोड़ें — अनियंत्रित मधुमेह जोखिम बढ़ाता है। 8. सर्जिकल साइट को जीभ/उंगली से न छुएं। 9. हर 6 महीने की क्लीनिंग न छोड़ें। 10. इम्प्लांट हिले, 1 सप्ताह से ज़्यादा दर्द, बुखार/स्राव या होंठ में सुन्नपन हो तो तुरंत संपर्क करें।';
    if (tn.includes('braces') || tn.includes('orthodontic') || tn.includes('aligner')) return '1. हर खाने के बाद (कम से कम दिन में 3 बार) ऑर्थोडॉन्टिक ब्रश से 45° कोण पर ब्रश करें। 2. ब्रैकेट के बीच सफाई के लिए रोज़ इंटरडेंटल ब्रश और फ्लॉस थ्रेडर उपयोग करें। 3. सभी मासिक एडजस्टमेंट अपॉइंटमेंट पर आएं। 4. रबर बैंड/रिटेनर बिल्कुल निर्देशानुसार पहनें। 5. ब्रेसेस निकलने के बाद सलाह अनुसार जीवन भर रिटेनर पहनें। 6. अलाइनर के लिए दिन में कम से कम 22 घंटे पहनें। 7. कड़ा खाना (गन्ना, कच्ची गाजर, बर्फ, पॉपकॉर्न) और चिपचिपा खाना (च्युइंग गम, टॉफी) न खाएं — ब्रैकेट टूटते/हटते हैं। 8. तार चुभे, एडजस्टमेंट के 5 दिन बाद भी दर्द हो या ब्रैकेट निकल जाए तो तुरंत संपर्क करें।';
    if (tn.includes('whitening') || tn.includes('bleaching')) return '1. व्हाइटनिंग के बाद 2 सप्ताह तक दी गई एंटी-सेंसिटिविटी टूथपेस्ट उपयोग करें। 2. उपचार के 48 घंटे तक "व्हाइट डाइट" लें — केवल सफेद/हल्के रंग का खाना। 3. 48 घंटे तक दाग लगाने वाली चीज़ें न लें: चाय, कॉफी, रेड वाइन, करी। 4. कम से कम 48 घंटे धूम्रपान न करें — तंबाकू तुरंत दोबारा दाग लगाता है। 5. गर्भावस्था, स्तनपान, 16 वर्ष से कम, या गंभीर मसूड़ों की बीमारी में व्हाइटनिंग न करवाएं। 6. 3 दिन से अधिक तेज़ संवेदनशीलता, गले में जलन, मसूड़ों में जलन/सफेद धब्बे या असमान व्हाइटनिंग हो तो तुरंत संपर्क करें।';
    if (tn.includes('denture')) return '1. रात में डेंचर निकालकर साफ पानी या डेंचर सॉल्यूशन में भिगोएं। 2. हर खाने के बाद मुलायम डेंचर ब्रश से डेंचर साफ करें। 3. हर दिन मुलायम ब्रश से मसूड़े, जीभ और तालू साफ करें। 4. नरम खाने से शुरू करें और दोनों तरफ एक साथ चबाने का अभ्यास करें। 5. डेंचर पहनकर न सोएं — इससे हड्डी घटती है। 6. साधारण टूथपेस्ट या गर्म/उबलते पानी से न साफ करें — सतह खराब होती है। 7. डेंचर को खुद ठीक न करें। 8. वार्षिक जांच के लिए आएं — रिलाइनिंग की ज़रूरत पड़ सकती है। 9. डेंचर टूटे, ढीला हो, मुंह के घाव 1 सप्ताह में न भरें या सांस/निगलने में तकलीफ हो तो तुरंत संपर्क करें।';
    if (tn.includes('rct') || tn.includes('root canal')) return '1. पूरी अवधि तक एंटीबायोटिक और एंटी-इंफ्लेमेटरी दवाइयां लें। 2. फाइनल क्राउन लगने तक दूसरी तरफ से चबाएं। 3. अच्छी मौखिक स्वच्छता बनाए रखें — दिन में दो बार धीरे-धीरे ब्रश करें। 4. सुझाए गए समय (2-4 सप्ताह) के भीतर स्थायी क्राउन लगवाएं — देरी से दांत टूट सकता है। 5. हर विजिट के बाद 2-5 दिनों तक हल्की तकलीफ/संवेदनशीलता सामान्य है। 6. इलाज की तरफ से कड़ा या कुरकुरा खाना न चबाएं — अस्थायी फिलिंग टूट सकती है। 7. भले ही दर्द कम हो जाए, एंटीबायोटिक बीच में न छोड़ें। 8. तीसरे दिन के बाद दर्द बढ़े, बुखार/मवाद का स्वाद हो, फिलिंग निकले या सूजन चेहरे/गर्दन तक फैले तो तुरंत संपर्क करें।';
    if (tn.includes('crown') || tn.includes('bridge') || tn.includes('veneer') || tn.includes('cap')) return '1. क्राउन की देखभाल प्राकृतिक दांत की तरह करें — रोज़ ब्रश और फ्लॉस करें। 2. क्राउन के किनारे पर सावधानी से फ्लॉस करें — मसूड़े की रेखा पर सड़न रोकने के लिए। 3. यदि दांत पीसने की आदत है तो नाइट गार्ड पहनें। 4. क्राउन से बहुत कड़ी चीज़ें (हड्डियां, कड़े मेवे, बर्फ) न चबाएं। 5. बोतल खोलने या पैकेट फाड़ने के लिए दांतों का उपयोग न करें। 6. अस्थायी क्राउन हो तो स्थायी क्राउन लगने तक दूसरी तरफ से चबाएं। 7. नियमित 6 महीने की जांच के लिए आएं। 8. क्राउन गिरे/ढीला हो, दरार दिखे, दर्द/संवेदनशीलता या मसूड़ों की सूजन हो तो तुरंत संपर्क करें।';
    if (tn.includes('filling') || tn.includes('composite') || tn.includes('amalgam') || tn.includes('gic')) return '1. कंपोज़िट फिलिंग के बाद कम से कम 2 घंटे तक कुछ न खाएं। अमलगम की तरफ से 24 घंटे बाद चबाएं। 2. 48 घंटे तक चिपचिपी चीज़ें (टॉफी, च्युइंग गम) न खाएं। 3. भरे हुए दांत से बर्फ, कड़ा खाना या पेन कैप न काटें। 4. गर्म/ठंडे के प्रति 1-2 सप्ताह तक हल्की संवेदनशीलता सामान्य है। 5. पहले दिन से ही भरे हुए दांत के आसपास धीरे-धीरे ब्रश करें। 6. काटने में "ऊंचा" लगे तो 24 घंटे में संपर्क करें। 7. फिलिंग निकल जाए, काटने पर तेज़ दर्द, सूजन या 3 सप्ताह बाद भी संवेदनशीलता हो तो तुरंत संपर्क करें।';
    if (tn.includes('extract')) return '1. खून रोकने के लिए पट्टी (गॉज) को 30-45 मिनट तक दबाकर रखें। 2. पहले 24 घंटे तक थूकें नहीं, कुल्ला न करें और स्ट्रॉ का उपयोग न करें। 3. दी गई दवाएं समय पर ले लें (एंटीबायोटिक और पेनकिलर)। 4. सूजन हो तो बाहर से ठंडी सिकाई करें (पहले दिन)। 5. पहले दिन ठंडा, नरम खाना खाएं, गर्म, तीखा या कठोर खाना न लें। 6. धूम्रपान और तंबाकू से कम से कम 72 घंटे (आदर्श रूप से 1 सप्ताह) दूर रहें। 7. घाव को उंगली या जीभ से छेड़ें नहीं। 8. ब्रश हल्के से करें, निकाले गए दांत के स्थान पर 24 घंटे तक न करें। 9. 24 घंटे बाद से गुनगुने नमक के पानी से कुल्ला करें — दिन में 3-4 बार। 10. एस्पिरिन/खून पतला करने वाली दवा डॉक्टर की सलाह बिना न लें। 11. 1 घंटे बाद भी खून न रुके, 3 दिन बाद सूजन बढ़े, दवा से दर्द न रुके, मवाद/बदबू, 101°F से अधिक बुखार या 8 घंटे से ज़्यादा सुन्नपन हो तो तुरंत संपर्क करें।';
    if (tn.includes('scaling') || tn.includes('polishing') || tn.includes('clean')) return '1. गर्म/ठंडे के प्रति हल्की संवेदनशीलता और 3-7 दिन तक मसूड़ों से हल्का खून आना बिल्कुल सामान्य है। 2. मुलायम ब्रिसल वाले टूथब्रश से दिन में दो बार धीरे-धीरे ब्रश करें। 3. 1 सप्ताह तक दिन में 2-3 बार गर्म नमक वाले पानी से कुल्ला करें। 4. 48 घंटे तक बहुत गर्म या ठंडा कुछ भी न खाएं-पिएं। 5. धूम्रपान या तंबाकू का सेवन न करें — यह मसूड़ों की बीमारी को बहुत बढ़ाता है। 6. कड़े ब्रिसल वाला ब्रश न उपयोग करें। 7. तुरंत परिणाम की उम्मीद न रखें — मसूड़े ठीक होने में 4-6 सप्ताह लगते हैं। 8. मसूड़ों से खून न रुके, फोड़ा/मवाद बने, 1 सप्ताह से अधिक तेज़ दर्द हो या दांत बहुत हिलने लगें तो तुरंत संपर्क करें। 9. हर 6 महीने में नियमित स्केलिंग करवाएं।';
    return '1. दी गई दवाएं समय पर लें। 2. 24 घंटे तक उपचारित तरफ से न चबाएं। 3. सूजन होने पर आइस पैक लगाएं। 4. मौखिक स्वच्छता बनाए रखें। 5. निर्धारित समय पर फॉलो-अप के लिए आएं। 6. गंभीर दर्द, सूजन या रक्तस्राव होने पर तुरंत संपर्क करें।';
  };

  // AI-generated procedure details based on treatment type
  const getProcedureDetails = (treatmentName) => {
    const tn = (treatmentName||'').toLowerCase();
    if (tn.includes('extract')) return '1. Local anesthesia administered (Lignocaine 2% with Adrenaline)\n2. Tooth extracted using forceps and elevators\n3. Hemostasis achieved with gauze pressure; sutures placed if required';
    if (tn.includes('rct') || tn.includes('root canal')) return '1. Local anesthesia administered\n2. Access opening made on the tooth\n3. Pulp tissue removed, canals cleaned and shaped\n4. Canals irrigated with NaOCl and saline\n5. Obturation done with gutta-percha\n6. Temporary restoration placed';
    if (tn.includes('scaling')) return '1. Ultrasonic scaling performed to remove supragingival and subgingival calculus\n2. Hand scaling done for residual deposits\n3. Polishing done with prophylaxis paste\n4. Oral hygiene instructions given';
    if (tn.includes('filling') || tn.includes('composite')) return '1. Local anesthesia administered\n2. Carious tooth structure removed\n3. Cavity prepared and etched\n4. Bonding agent applied\n5. Composite resin placed and light cured\n6. Occlusion checked and adjusted';
    if (tn.includes('crown') || tn.includes('cap')) return '1. Local anesthesia administered\n2. Tooth prepared/reduced for crown\n3. Impression taken\n4. Temporary crown placed\n5. Crown to be cemented on next visit';
    if (tn.includes('implant')) return '1. Local anesthesia administered\n2. Flap raised\n3. Osteotomy performed\n4. Implant placed and torqued to stability\n5. Cover screw placed\n6. Flap sutured\n7. Post-operative radiograph taken';
    return '1. Local anesthesia administered as required\n2. Procedure performed under aseptic conditions\n3. Patient monitored and discharged in stable condition';
  };

  // Print discharge summary — matches Siddharth Dental Hospital format
  const handlePrintDischarge = async (tx) => {
    const printMode = await askPrintMode();
    const isLetterhead = printMode === 'letterhead';
    const pt = selectedPatient;
    const ptName = (pt.title?pt.title+' ':'')+pt.name;
    const rx = pastPrescriptions.find(p => String(p._id) === String(tx?.prescriptionId));
    const dischargeMeds = rx?.medicines || tx?.medicines || [];
    const hospitalMeds = tx?.treatmentName?.toLowerCase().includes('extract')
      ? [{name:'Local anesthetic: Lignocaine 2% with adrenaline',dosage:'',duration:'3 ML',instructions:'IANC'}]
      : [{name:'Local anesthetic: Lignocaine 2% with adrenaline',dosage:'',duration:'1.5 ML',instructions:'Infiltration'}];
    const todayStr = new Date().toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'});
    const doneStr = tx?.doneDate ? new Date(tx.doneDate).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}) : todayStr;
    // AI-generated chief complaint based on treatment type (if doctor didn't enter one)
    const getAIChiefComplaint = (treatmentName) => {
      const tn = (treatmentName||'').toLowerCase();
      if (tn.includes('extract') && tn.includes('surg')) return ['Pain and swelling in relation to impacted tooth','Difficulty in mouth opening','Recurrent food lodgement'];
      if (tn.includes('extract')) return ['Severe toothache','Grossly decayed tooth','Swelling / pus discharge near tooth'];
      if (tn.includes('rct') || tn.includes('root canal')) return ['Spontaneous throbbing toothache','Lingering pain to hot/cold','Pain on biting','Disturbed sleep due to pain'];
      if (tn.includes('scaling') || tn.includes('clean')) return ['Bleeding gums during brushing','Bad breath (Halitosis)','Deposits on teeth','Gum swelling and redness'];
      if (tn.includes('filling') || tn.includes('composite') || tn.includes('gic')) return ['Food lodgement in tooth','Mild sensitivity to sweet/cold','Visible cavity in tooth'];
      if (tn.includes('crown') || tn.includes('cap')) return ['Fractured / weakened tooth after RCT','Tooth structure loss requiring protection','Aesthetic concern'];
      if (tn.includes('bridge')) return ['Missing tooth causing chewing difficulty','Aesthetic concern due to missing tooth','Tilting of adjacent teeth'];
      if (tn.includes('implant')) return ['Missing tooth requiring permanent replacement','Difficulty in chewing due to missing tooth','Aesthetic concern due to tooth loss'];
      if (tn.includes('denture')) return ['Multiple missing teeth','Difficulty in chewing and speaking','Aesthetic concern due to missing teeth'];
      if (tn.includes('braces') || tn.includes('ortho')) return ['Irregular / crooked teeth','Spacing / crowding of teeth','Protruding front teeth','Difficulty in maintaining oral hygiene'];
      if (tn.includes('whitening') || tn.includes('bleach')) return ['Discolored / stained teeth','Aesthetic concern — desire for whiter teeth'];
      if (tn.includes('pulpotomy') || tn.includes('pulp cap')) return ['Toothache on eating','Deep cavity approaching nerve','Sensitivity to hot/cold'];
      if (tn.includes('flap')) return ['Loose teeth','Gum recession','Deep gum pockets','Bone loss around teeth'];
      if (tn.includes('apico')) return ['Persistent pain after root canal treatment','Swelling that recurs despite RCT','Non-healing sinus tract'];
      if (tn.includes('veneer') || tn.includes('laminate')) return ['Discolored front teeth','Chipped / uneven front teeth','Aesthetic improvement desired'];
      if (tn.includes('frenectomy')) return ['Gap between front teeth due to thick frenum','Restricted tongue movement','Difficulty in speech'];
      return ['Dental concern requiring evaluation and treatment'];
    };

    const ccList = [];
    // Check if chiefComplaint is actually a treatment type name (bug: auto-created rx stores treatment name as CC)
    const ttNames = (treatmentTypes||[]).map(t => t.name.toLowerCase());
    const rawCC = (rx?.chiefComplaint||'').trim();
    const ccIsTreatmentName = rawCC && ttNames.some(tn => rawCC.toLowerCase() === tn || rawCC.toLowerCase().includes(tn) || tn.includes(rawCC.toLowerCase()));
    if (rawCC && !ccIsTreatmentName && rawCC.length > 3) {
      rawCC.split(',').forEach(c => ccList.push(c.trim()));
    } else {
      getAIChiefComplaint(tx?.treatmentName).forEach(c => ccList.push(c));
    }

    // AI-generated provisional & final diagnosis based on treatment type
    const getAIDiagnosis = (treatmentName, type) => {
      const tn = (treatmentName||'').toLowerCase();
      const diag = {
        prov: '', final: '', reason: ''
      };
      if (tn.includes('extract') && tn.includes('surg')) {
        diag.prov = 'Impacted / Semi-impacted third molar with pericoronitis';
        diag.final = 'Impacted third molar requiring surgical intervention';
        diag.reason = `Admitted for surgical extraction of ${tx?.toothNumber?'tooth #'+tx.toothNumber+' — ':''}impacted/semi-impacted tooth causing recurrent pericoronitis and pain, not amenable to conservative management.`;
      } else if (tn.includes('extract')) {
        diag.prov = 'Periapical abscess associated with grossly decayed tooth, non-restorable';
        diag.final = 'Grossly decayed tooth with pulpal necrosis and periapical pathology';
        diag.reason = `Admitted for extraction of ${tx?.toothNumber?'tooth #'+tx.toothNumber+' — ':''}a grossly decayed tooth with pulpal involvement and poor prognosis, non-restorable through conservative means.`;
      } else if (tn.includes('rct') || tn.includes('root canal')) {
        diag.prov = 'Irreversible pulpitis with symptomatic apical periodontitis';
        diag.final = 'Irreversible pulpitis / Pulp necrosis with periapical radiolucency';
        diag.reason = `Admitted for endodontic treatment (Root Canal) of ${tx?.toothNumber?'tooth #'+tx.toothNumber+' — ':''}presenting with irreversible pulpitis and periapical involvement.`;
      } else if (tn.includes('scaling') || tn.includes('clean')) {
        diag.prov = 'Chronic generalized gingivitis with calculus deposits';
        diag.final = 'Chronic generalized gingivitis / Chronic periodontitis (mild to moderate)';
        diag.reason = `Admitted for professional scaling and prophylaxis to treat generalized calculus deposits and gingival inflammation.`;
      } else if (tn.includes('filling') || tn.includes('composite') || tn.includes('gic')) {
        diag.prov = 'Dental caries involving dentin';
        diag.final = 'Dental caries (Class I/II) — dentin involvement without pulp exposure';
        diag.reason = `Admitted for restorative treatment of ${tx?.toothNumber?'tooth #'+tx.toothNumber+' — ':''}carious lesion requiring direct restoration.`;
      } else if (tn.includes('crown') || tn.includes('cap')) {
        diag.prov = 'Structurally compromised tooth requiring full coverage restoration';
        diag.final = 'Post-endodontic / Fractured tooth requiring prosthetic rehabilitation (Crown)';
        diag.reason = `Admitted for crown preparation and placement on ${tx?.toothNumber?'tooth #'+tx.toothNumber+' — ':''}structurally weakened tooth requiring full coverage protection.`;
      } else if (tn.includes('bridge')) {
        diag.prov = 'Partially edentulous arch requiring fixed prosthetic rehabilitation';
        diag.final = 'Missing tooth/teeth — Kennedy Class classification requiring fixed partial denture';
        diag.reason = `Admitted for fixed bridge fabrication to replace missing tooth/teeth and restore masticatory function and aesthetics.`;
      } else if (tn.includes('implant')) {
        diag.prov = 'Edentulous site with adequate bone for implant supported prosthesis';
        diag.final = 'Missing tooth with sufficient alveolar bone — suitable for endosseous implant placement';
        diag.reason = `Admitted for dental implant placement at ${tx?.toothNumber?'site #'+tx.toothNumber+' — ':''}edentulous region with adequate bone volume for implant supported prosthesis.`;
      } else if (tn.includes('denture')) {
        diag.prov = 'Complete / Partial edentulism requiring removable prosthesis';
        diag.final = 'Edentulous / Partially edentulous arch — prosthetic rehabilitation with removable denture';
        diag.reason = `Admitted for fabrication and delivery of removable prosthesis to restore masticatory function, aesthetics, and phonetics.`;
      } else if (tn.includes('braces') || tn.includes('ortho')) {
        diag.prov = 'Malocclusion requiring orthodontic intervention';
        diag.final = 'Angle\'s Class I/II/III Malocclusion with crowding/spacing';
        diag.reason = `Admitted for orthodontic treatment to correct malocclusion, improve dental alignment and occlusal relationships.`;
      } else if (tn.includes('whitening') || tn.includes('bleach')) {
        diag.prov = 'Tooth discoloration — extrinsic / intrinsic staining';
        diag.final = 'Extrinsic staining / Mild intrinsic discoloration amenable to bleaching';
        diag.reason = `Admitted for professional tooth whitening/bleaching procedure to improve dental aesthetics.`;
      } else if (tn.includes('pulpotomy') || tn.includes('pulp cap')) {
        diag.prov = 'Reversible pulpitis / Carious pulp exposure (vital tooth)';
        diag.final = 'Carious exposure of vital pulp — suitable for vital pulp therapy';
        diag.reason = `Admitted for pulpotomy/pulp capping on ${tx?.toothNumber?'tooth #'+tx.toothNumber+' — ':''}to preserve pulp vitality and avoid root canal treatment.`;
      } else if (tn.includes('flap') || tn.includes('surgery')) {
        diag.prov = 'Chronic periodontitis with deep periodontal pockets and bone loss';
        diag.final = 'Chronic periodontitis (moderate to severe) requiring surgical intervention';
        diag.reason = `Admitted for periodontal flap surgery to access and debride deep pockets and bone defects not responsive to non-surgical therapy.`;
      } else if (tn.includes('apico')) {
        diag.prov = 'Persistent periapical pathology post endodontic treatment';
        diag.final = 'Failed endodontic treatment / Periapical cyst requiring surgical intervention';
        diag.reason = `Admitted for apicoectomy on ${tx?.toothNumber?'tooth #'+tx.toothNumber+' — ':''}persistent periapical lesion not resolved by conventional retreatment.`;
      } else if (tn.includes('veneer') || tn.includes('laminate')) {
        diag.prov = 'Aesthetically compromised anterior teeth';
        diag.final = 'Enamel defects / Discoloration / Diastema requiring porcelain/composite veneer';
        diag.reason = `Admitted for veneer placement to restore aesthetics of anterior teeth.`;
      } else if (tn.includes('frenectomy')) {
        diag.prov = 'Aberrant frenum attachment causing diastema / restricted tongue movement';
        diag.final = 'High frenal attachment / Ankyloglossia requiring frenectomy';
        diag.reason = `Admitted for frenectomy to release aberrant frenal attachment.`;
      } else {
        diag.prov = rx?.diagnosis || 'Clinical evaluation pending detailed investigation';
        diag.final = rx?.diagnosis || 'As per clinical findings and investigation results';
        diag.reason = `Admitted for ${tx?.treatmentName||'dental treatment'}${tx?.toothNumber?' on tooth #'+tx.toothNumber:''}.`;
      }
      // If prescription has a real diagnosis, use that for final
      if (rx?.diagnosis && rx.diagnosis.length > 5) {
        diag.final = rx.diagnosis;
      }
      return diag;
    };
    const aiDiag = getAIDiagnosis(tx?.treatmentName);
    const provDiag = aiDiag.prov;
    const finalDiag = aiDiag.final;
    const reasonAdmission = aiDiag.reason;
    const investigations = rx?.investigation ? rx.investigation : 'Not Required';
    const condOnDischarge = tx?.treatmentName?.toLowerCase().includes('extract')
      ? '1. Stable — Hemostasis maintained. No active bleeding or swelling.\n2. Post-op instructions explained (avoid hot food, biting, etc.)'
      : '1. Stable — No complications observed.\n2. Post-op instructions explained to patient/guardian.';
    const hospitalCourse = tx?.treatmentName?.toLowerCase().includes('extract')
      ? '1. Procedure done under aseptic conditions\n2. No excessive bleeding or complications\n3. Patient kept under observation and discharged after stability confirmed'
      : '1. Procedure done under aseptic conditions\n2. No complications observed\n3. Patient discharged in stable condition';
    const procedureDetails = getProcedureDetails(tx?.treatmentName);
    const postOpText = getPostOpInstructions(tx?.treatmentName);

    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>Discharge Summary — ${ptName}</title>
    <style>
      @page { size: A4; margin: 10mm; }
      * { box-sizing: border-box; margin: 0; padding: 0; }
      body { font-family: Arial, sans-serif; color: #000; font-size: 11px; line-height: 1.4; }
      .page { max-width: 760px; margin: 0 auto; padding: 10px; }
      h1 { text-align: center; font-size: 20px; font-weight: bold; text-decoration: underline; margin-bottom: 8px; letter-spacing: 2px; }
      .meta-table { width: 100%; border-collapse: collapse; margin-bottom: 8px; }
      .meta-table td { border: 1px solid #000; padding: 3px 6px; font-size: 11px; }
      .meta-label { font-weight: bold; width: 130px; }
      .grid4 { display: grid; grid-template-columns: 1fr 1fr 1fr 1fr; border: 1px solid #000; margin-bottom: 8px; }
      .grid4 > div { border: 1px solid #000; padding: 6px 8px; min-height: 80px; }
      .grid4 h4 { font-size: 11px; text-decoration: underline; margin-bottom: 4px; }
      .grid2 { display: grid; grid-template-columns: 1fr 1fr; border: 1px solid #000; margin-bottom: 8px; }
      .grid2 > div { border: 1px solid #000; padding: 6px 8px; min-height: 60px; }
      .grid2 h4 { font-size: 11px; text-decoration: underline; margin-bottom: 4px; }
      .grid3 { display: grid; grid-template-columns: 1fr 1fr 1.5fr; border: 1px solid #000; margin-bottom: 8px; }
      .grid3 > div { border: 1px solid #000; padding: 6px 8px; min-height: 60px; }
      .grid3 h4 { font-size: 11px; text-decoration: underline; margin-bottom: 4px; }
      .section-box { border: 1px solid #000; padding: 6px 8px; margin-bottom: 8px; }
      .section-box h4 { font-size: 11px; text-decoration: underline; margin-bottom: 4px; }
      .med-table { width: 100%; border-collapse: collapse; margin: 6px 0; }
      .med-table th, .med-table td { border: 1px solid #000; padding: 4px 6px; font-size: 10px; text-align: left; }
      .med-table th { font-weight: bold; font-size: 10px; }
      .ack-box { border: 2px solid #000; padding: 10px; margin: 10px 0; font-size: 10px; }
      .ack-box h4 { text-align: center; text-decoration: underline; margin-bottom: 6px; }
      .sign-row { display: flex; justify-content: space-between; margin-top: 20px; font-size: 11px; }
      .postop { border: 1px solid #000; padding: 8px; margin-bottom: 8px; }
      .postop h4 { text-align: center; text-decoration: underline; margin-bottom: 6px; }
      @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
    </style></head><body>
    <div class="page">

    ${isLetterhead ? '<div style="height:80px"></div>' : `<div style="text-align:center;padding:10px;border-bottom:3px double #000;margin-bottom:8px">
      <div style="font-size:20px;font-weight:900;letter-spacing:2px">${(clinic?.clinicName || 'Your Clinic').toUpperCase()}</div>
      <div style="font-size:9px;color:#555">${[clinic?.clinicAddress, clinic?.clinicPhone && 'Ph: ' + clinic.clinicPhone].filter(Boolean).join(' | ')}</div>
    </div>`}

    <h1>DISCHARGE SUMMARY</h1>

    <!-- Meta info table -->
    <table class="meta-table">
      <tr>
        <td class="meta-label">OPD NO.</td><td>${pt.uhid||''}</td>
        <td class="meta-label">DATE OF ADMISSION:</td><td>${doneStr}</td>
      </tr>
      <tr>
        <td class="meta-label">DAYCARE NO.</td><td>${pt.uhid||''}</td>
        <td class="meta-label">DATE OF DISCHARGE:</td><td>${todayStr}</td>
      </tr>
      <tr>
        <td class="meta-label">AGE:</td><td>${pt.age||''}</td>
        <td class="meta-label">PATIENT NAME:</td><td><strong>${ptName}</strong></td>
      </tr>
      <tr>
        <td class="meta-label">ADDRESS:</td><td colspan="3">${pt.address||''}</td>
      </tr>
    </table>

    <!-- Chief Complaint / Medical History / Dental History / Allergies -->
    <div class="grid4">
      <div><h4>Chief Complaint</h4>${ccList.map(c=>`<div>${c}</div>`).join('')}</div>
      <div><h4>Medical History</h4>${(pt.medicalHistory||[]).length>0?(pt.medicalHistory||[]).join(', '):'NA'}</div>
      <div><h4>Dental History</h4>${(pt.dentalHistory||[]).length>0?(pt.dentalHistory||[]).join(', '):'NA'}</div>
      <div><h4>Allergies</h4>${(pt.allergies||[]).length>0?(pt.allergies||[]).join(', '):'NA'}</div>
    </div>

    <!-- Investigations -->
    <div class="section-box">
      <h4>Investigations</h4>
      <div style="font-size:13px">${investigations}</div>
    </div>

    <!-- Reason for Admission -->
    <div class="section-box">
      <h4>Reason for Admission</h4>
      <div>${reasonAdmission}</div>
    </div>

    <!-- Provisional Diagnosis / Final Diagnosis / Details of Procedure -->
    <div class="grid3">
      <div><h4>Provisional Diagnosis</h4>${provDiag}</div>
      <div><h4>Final Diagnosis</h4>${finalDiag}</div>
      <div><h4>Details of Procedure</h4>${procedureDetails.split('\n').map(l=>`<div>${l}</div>`).join('')}</div>
    </div>

    <!-- Condition On Discharge / Hospital Course -->
    <div class="grid2">
      <div><h4>Condition On Discharge</h4>${condOnDischarge.split('\n').map(l=>`<div>${l}</div>`).join('')}</div>
      <div><h4>Hospital Course</h4>${hospitalCourse.split('\n').map(l=>`<div>${l}</div>`).join('')}</div>
    </div>

    <!-- Medication During Hospital Course -->
    <div style="font-weight:bold;font-size:12px;margin:10px 0 4px">MEDICATION DURING HOSPITAL COURSE</div>
    <table class="med-table">
      <thead><tr><th>S No.</th><th>FORMULATION</th><th>DOSE</th><th>ROUTE</th><th>DIRECTION/TIME</th></tr></thead>
      <tbody>${hospitalMeds.map((m,i)=>`<tr><td>${i+1}</td><td>${m.name}</td><td>${m.duration||'-'}</td><td>${m.instructions||'-'}</td><td></td></tr>`).join('')}</tbody>
    </table>

    <!-- Medication Advised On Discharge -->
    <div style="font-weight:bold;font-size:12px;margin:10px 0 4px">MEDICATION ADVISED ON DISCHARGE</div>
    <table class="med-table">
      <thead><tr><th>S No.</th><th>FORMULATION</th><th>DOSE</th><th>ROUTE</th><th>DIRECTION/TIME</th></tr></thead>
      <tbody>${dischargeMeds.length>0?dischargeMeds.map((m,i)=>`<tr><td>${i+1}</td><td>${m.name}</td><td>${m.dosage||'-'}</td><td>Oral</td><td>${m.instructions||'-'}</td></tr>`).join(''):'<tr><td colspan="5" style="text-align:center">No medications prescribed</td></tr>'}</tbody>
    </table>

    <!-- Post-Operative Instructions -->
    <div class="postop">
      <h4>Post-Operative Instructions:</h4>
      <div style="font-size:10px;line-height:1.5">${postOpText}</div>
    </div>

    <!-- Acknowledgment -->
    <div class="ack-box">
      <h4>Acknowledgment</h4>
      <div>I, <strong>${ptName}</strong>, acknowledge that I have received and understood the information provided in this discharge summary, including postoperative care instructions and potential signs of complications. I understand the importance of following these instructions and the need for follow-up care. I have had the opportunity to ask questions and discuss my treatment with my clinician.</div>
    </div>

    <!-- Signatures -->
    <div class="sign-row">
      <div>Patient Signature____________________</div>
      <div>Doctor Signature____________________</div>
    </div>

    </div>
    <script>window.onload=function(){window.print();window.close();}</script>
    </body></html>`);
    w.document.close();
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete patient "${selectedPatient.name}"? This will remove ALL data including bills, treatments, prescriptions. This cannot be undone.`)) return;
    try {
      await axios.delete(`${API_BASE}/patients/${selectedPatient._id}`);
      resetViews();
      fetchAllPatients();
      alert('Patient deleted');
    } catch (e) { alert('Delete failed'); }
  };

  /* ═════════════════════════ RENDER ═════════════════════════ */

  if (!selectedPatient) return null;

  /* Computed helpers for the UI */
  const fullName = (selectedPatient.title ? selectedPatient.title + ' ' : '') + selectedPatient.name;
  const hasDueBalance = ptOutstanding && ptOutstanding.outstanding > 0;
  const plannedTx = pastTreatments.filter(t => t.status === 'planned');
  const doneTx = pastTreatments.filter(t => t.status === 'done');
  const familyCount = patientsList.filter(p => p.familyId && p.familyId === selectedPatient.familyId).length;
  const medicalCount = (selectedPatient.medicalHistory || []).length;
  const allergyCount = (selectedPatient.allergies || []).length;
  const hasSafetyAlerts = medicalCount > 0 || allergyCount > 0;

  // Top tabs reduced to 5 — only the most-used clinical workflows.
  // Visits, Vitals, Comms, Documents, Notes, Feedback, Forms moved to sidebar
  // quick-access (collapsible sidebar that expands on hover).
  const TABS = [
    { key: 'overview', label: 'Overview',  icon: Activity },
    { key: 'chart',    label: 'Chart',     icon: Activity },
    { key: 'clinical', label: 'Clinical',  icon: Stethoscope },
    { key: 'lab-work', label: 'Lab Work',  icon: FlaskConical },
    { key: 'pharmacy', label: 'Pharmacy',  icon: CreditCard },
  ];

  // Sub-tabs inside the "Clinical" main tab. Vitals removed (now in sidebar).
  const CLINICAL_SUB_TABS = [
    { key: 'imaging',   label: 'Imaging' },
    { key: 'ortho',     label: 'Ortho' },
    { key: 'implants',  label: 'Implants' },
    { key: 'perio',     label: 'Perio' },
    { key: 'plans',     label: 'Plans' },
    { key: 'education', label: 'Education' },
    { key: 'referrals', label: 'Referrals' },
  ];

  // Sidebar quick-access buttons — open in main pane area.
  const SIDEBAR_QUICK_TABS = [
    { key: 'visits',    label: 'Visits',         icon: Stethoscope },
    { key: 'vitals',    label: 'Vitals',         icon: Activity },
    { key: 'comms',     label: 'Communications', icon: MessageCircle },
    { key: 'calls',     label: 'Calls',          icon: Phone },
    { key: 'documents', label: 'Documents',      icon: FileText },
    { key: 'notes',     label: 'Notes',          icon: Edit3 },
    { key: 'feedback',  label: 'Feedback',       icon: Star },
    { key: 'forms',     label: 'Forms',          icon: ClipboardList },
  ];
  // ClinicalPage tab key for each sub-tab that maps to a Clinical Suite tab.
  const CLINICAL_TABS_MAP = {
    ortho:     'ortho',
    implants:  'implants',
    perio:     'perio',
    education: 'edu',
    referrals: 'referrals',
    plans:     'plans',
  };
  const activeKey = patientTab || 'overview';
  const goTab = (k) => { setPatientTab(k === 'overview' ? '' : k); setIsWritingPrescription(false); setIsWritingBill(false); };

  return (
    <div style={S.root}>
      {/* ═══ Top bar ═══ */}
      <div style={S.topBar}>
        <button onClick={resetViews} style={S.backBtn}>
          <ArrowLeft size={16}/> Back to patients
        </button>

        <div style={S.tabBar}>
          {TABS.map(t => {
            const Icon = t.icon;
            const isActive = activeKey === t.key;
            return (
              <button key={t.key} onClick={() => goTab(t.key)}
                style={{ ...S.tabBtn, ...(isActive ? S.tabBtnActive : {}) }}>
                <Icon size={14}/> {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* ═══ Main split: sidebar + content (sidebar collapses to icons, expands on hover) ═══ */}
      <div style={{ ...S.split, gridTemplateColumns: sidebarHover ? '320px 1fr' : '72px 1fr', transition: 'grid-template-columns 0.2s ease' }}>

        {/* ───── LEFT SIDEBAR ───── (collapsed icons by default, expands on hover) */}
        <aside
          onMouseEnter={() => setSidebarHover(true)}
          onMouseLeave={() => setSidebarHover(false)}
          style={{ ...S.sidebar, overflowX: 'hidden', overflowY: 'auto', transition: 'all 0.2s ease' }}>
          {/* Photo + identity — photo always shown, smaller when collapsed; details only when expanded */}
          <div style={{ ...S.identityBlock, padding: sidebarHover ? '24px 20px 20px' : '14px 8px 10px' }} title={!sidebarHover ? `${fullName} · ${selectedPatient.uhid}` : undefined}>
            {selectedPatient.photo ? (
              <img src={/^https?:\/\//.test(selectedPatient.photo) ? selectedPatient.photo : `${UPLOAD_BASE}/${selectedPatient.photo}`} alt={fullName}
                style={{ ...S.photo, width: sidebarHover ? 90 : 44, height: sidebarHover ? 90 : 44, marginBottom: sidebarHover ? 12 : 0 }}
                onError={e => { e.target.onerror = null; e.target.style.display = 'none'; }}/>
            ) : (
              <div style={{ ...S.photoFallback, width: sidebarHover ? 90 : 44, height: sidebarHover ? 90 : 44, fontSize: sidebarHover ? '2.2rem' : '1.1rem', marginBottom: sidebarHover ? 12 : 0 }}>
                {(selectedPatient.name || '?').charAt(0).toUpperCase()}
              </div>
            )}
            {sidebarHover && <>
              <div style={S.name}>{fullName}</div>
              <div style={S.uhid}>UHID · {selectedPatient.uhid}</div>
              <div style={S.metaRow}>
                {selectedPatient.age && <span>{selectedPatient.age}y</span>}
                {selectedPatient.gender && <span>·</span>}
                {selectedPatient.gender && <span>{selectedPatient.gender}</span>}
              </div>
            </>}
          </div>

          {/* Outstanding balance — full when expanded, compact dot when collapsed */}
          {ptOutstanding && (sidebarHover ? (
            hasDueBalance ? (
              <button onClick={() => setPaymentOpen(true)} style={S.dueBadge}>
                <div>
                  <div style={S.dueLabel}>Balance Due</div>
                  <div style={S.dueAmount}>₹{(ptOutstanding.outstanding || 0).toLocaleString('en-IN')}</div>
                </div>
                <CreditCard size={18} />
              </button>
            ) : (
              <div style={S.paidBadge}>✓ Fully paid</div>
            )
          ) : (
            // Collapsed: small icon-only indicator
            <div title={hasDueBalance ? `Balance Due ₹${(ptOutstanding.outstanding || 0).toLocaleString('en-IN')}` : 'Fully paid'}
              style={{ display: 'flex', justifyContent: 'center', padding: '6px 0' }}>
              <CreditCard size={18} color={hasDueBalance ? theme.danger : theme.success} />
            </div>
          ))}

          {/* OPD / Registration fee validity — only when expanded */}
          {sidebarHover && opdStatus?.enabled && (
            opdStatus.valid ? (
              <div style={{padding:'8px 12px', backgroundColor:'#e8f7ef', border:'1px solid #c3e6cb',
                borderRadius:'8px', marginTop:'8px', fontSize:'0.8rem', color:'#155724'}}>
                ✓ OPD valid till{' '}
                <strong>{new Date(opdStatus.validTill).toLocaleDateString('en-IN',
                  {day:'2-digit', month:'short'})}</strong>
              </div>
            ) : (
              <button onClick={handleChargeOpdFee}
                style={{padding:'8px 12px', backgroundColor:'#fff3cd', border:'1px solid #ffc107',
                  borderRadius:'8px', marginTop:'8px', fontSize:'0.8rem', color:'#856404',
                  width:'100%', cursor:'pointer', textAlign:'left', fontWeight:600}}>
                ⚠️ OPD expired — Charge ₹{opdStatus.fee}
              </button>
            )
          )}

          {/* Safety alerts — full block when expanded, just icon badge when collapsed */}
          {hasSafetyAlerts && !sidebarHover && (
            <div title={`Safety alerts: ${allergyCount} allergy, ${medicalCount} medical`}
              style={{ display: 'flex', justifyContent: 'center', padding: '6px 0' }}>
              <AlertTriangle size={18} color={theme.danger} />
            </div>
          )}
          {hasSafetyAlerts && sidebarHover && (
            <div style={S.safetyBlock}>
              <div style={S.safetyHead}>
                <AlertTriangle size={13}/> Safety alerts
              </div>
              {allergyCount > 0 && (
                <div style={S.chipRow}>
                  {(selectedPatient.allergies || []).map((a, i) => (
                    <span key={i} style={S.chipAllergy}>{a}</span>
                  ))}
                </div>
              )}
              {medicalCount > 0 && (
                <div style={{ ...S.chipRow, marginTop: allergyCount ? 6 : 0 }}>
                  {(selectedPatient.medicalHistory || []).map((m, i) => (
                    <span key={i} style={S.chipMedical}>{m}</span>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Current medicines — only when expanded */}
          {sidebarHover && (selectedPatient.currentMedicines || []).length > 0 && (
            <SidebarSection title="Current medicines" icon={Pill}>
              <div style={S.chipRow}>
                {(selectedPatient.currentMedicines || []).map((m, i) => (
                  <span key={i} style={S.chipNeutral}>{m}</span>
                ))}
              </div>
            </SidebarSection>
          )}

          {/* Contact — only when expanded */}
          {sidebarHover && <SidebarSection title="Contact" icon={Phone}>
            {selectedPatient.phone && (
              <div style={S.sideRow}>
                <Phone size={13}/> <a href={`tel:${selectedPatient.phone}`} style={S.sideLink}>{selectedPatient.phone}</a>
              </div>
            )}
            {selectedPatient.dob && (
              <div style={S.sideRow}>
                <Cake size={13}/> {new Date(selectedPatient.dob).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
              </div>
            )}
            {selectedPatient.address && (
              <div style={S.sideRow}>
                <MapPin size={13} style={{ flexShrink: 0, marginTop: 2 }}/>
                <span>{selectedPatient.address}</span>
              </div>
            )}
            {selectedPatient.occupation && (
              <div style={S.sideRow}>
                <Activity size={13}/> {selectedPatient.occupation}
              </div>
            )}
          </SidebarSection>}

          {/* Family — only when expanded */}
          {sidebarHover && selectedPatient.familyId && (
            <SidebarSection title="Family" icon={UsersIcon}>
              <div style={S.sideRow}>
                <span style={S.chipFamily}>{selectedPatient.familyId}</span>
                {selectedPatient.familyRelation && <span style={S.chipFamilyRel}>{selectedPatient.familyRelation}</span>}
              </div>
              {familyCount > 1 && (
                <div style={{ ...S.sideRow, color: theme.textMuted, fontSize: 12 }}>{familyCount} members linked</div>
              )}
            </SidebarSection>
          )}

          {/* Groups / Tags — only when expanded */}
          {sidebarHover && ((selectedPatient.groups || []).length > 0 || (selectedPatient.tags || []).length > 0) && (
            <SidebarSection title="Groups & tags" icon={Tag}>
              <div style={S.chipRow}>
                {(selectedPatient.groups || []).map((g, i) => (
                  <span key={`g-${i}`} style={S.chipGroup}>{g}</span>
                ))}
                {(selectedPatient.tags || []).map((t, i) => (
                  <span key={`t-${i}`} style={S.chipTag}>#{t}</span>
                ))}
              </div>
            </SidebarSection>
          )}

          {/* ── Sidebar quick-access boxes — own scrollable region so it never gets clipped ── */}
          <div style={{ marginTop: 12, padding: '12px 8px', borderTop: `1px solid ${theme.borderLight}`, maxHeight: 360, overflowY: 'auto', overflowX: 'hidden' }}>
            {sidebarHover && (
              <div style={{ fontSize: 11, color: theme.textMuted, fontWeight: 700, textTransform: 'uppercase', padding: '0 6px 8px', letterSpacing: '0.05em' }}>
                Quick Access
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {SIDEBAR_QUICK_TABS.map(s => {
                const Ic = s.icon;
                const isActive = activeKey === s.key;
                return (
                  <button key={s.key}
                    onClick={() => goTab(s.key)}
                    title={!sidebarHover ? s.label : undefined}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: sidebarHover ? '8px 12px' : '10px 0',
                      justifyContent: sidebarHover ? 'flex-start' : 'center',
                      borderRadius: 8, border: 'none', cursor: 'pointer',
                      background: isActive ? theme.primaryLight : 'transparent',
                      color: isActive ? theme.primaryDark : theme.textMuted,
                      fontWeight: isActive ? 700 : 500, fontSize: 13,
                      transition: 'all 0.15s', flexShrink: 0,
                    }}
                    onMouseEnter={e => { if (!isActive) e.currentTarget.style.backgroundColor = '#f7f9fc'; }}
                    onMouseLeave={e => { if (!isActive) e.currentTarget.style.backgroundColor = 'transparent'; }}>
                    <Ic size={16} style={{ flexShrink: 0 }} />
                    {sidebarHover && <span style={{ whiteSpace: 'nowrap' }}>{s.label}</span>}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Quick actions, menus and edit/delete have moved to the floating action drawer on the right side. */}
        </aside>

        {/* ───── RIGHT CONTENT ───── */}
        <main style={S.content}>
          {activeKey === 'overview' && (
            <OverviewPane
              S={S}
              selectedPatient={selectedPatient}
              ptOutstanding={ptOutstanding}
              pastTreatments={pastTreatments}
              plannedTx={plannedTx}
              doneTx={doneTx}
              pastPrescriptions={pastPrescriptions}
              pastBills={pastBills}
              handlePrintRx={handlePrintRx}
              goTab={goTab}
              openPayments={() => setPaymentOpen(true)}
            />
          )}

          {activeKey === 'visits' && (
            <div style={S.paneCard}>
              <PaneHeader title="Visits timeline" subtitle="Every visit with treatments, notes and outcomes."/>
              <PatientVisitView
                patientId={selectedPatient._id}
                patientName={fullName}
                patientPhone={selectedPatient.phone}
                API_BASE={API_BASE}
                treatmentTypes={treatmentTypes}
                instructionList={instructionList}
                medicineLibrary={medicineLibrary}
                chiefComplaintList={chiefComplaintList}
                findingsList={findingsList}
                investigationList={investigationList}
                diagnosisList={diagnosisList}
                triggerNewVisit={newVisitTrigger}
                hideInternalAddButton
                whatsappTreatmentDone={(tt, tooth, meds, instrs, amt) => whatsappTreatmentDone(selectedPatient, tt, tooth, meds, instrs, amt)}
                whatsappTreatmentPlan={(tt, tooth, amt) => whatsappTreatmentPlan(selectedPatient, tt, tooth, amt)}
                onRefresh={() => {
                  fetchPastPrescriptions(selectedPatient._id);
                  axios.get(`${API_BASE}/payments/patient/${selectedPatient._id}/outstanding`).then(r => setPtOutstanding(r.data)).catch(() => {});
                }}
              />
            </div>
          )}

          {/* Rx and Bills are accessed inline from the Visits timeline and the floating action drawer (recording/writing); the due-balance badge opens Payments in a modal. */}

          {activeKey === 'chart' && (
            <div style={S.paneCard}>
              <PaneHeader title="Dental chart" subtitle="Odontogram — tooth-by-tooth status and treatments."/>
              <Odontogram key={selectedPatient._id} patientId={selectedPatient._id} API_BASE={API_BASE}/>
            </div>
          )}

          {activeKey === 'documents' && (
            <div style={S.paneCard}>
              <PaneHeader title="Documents & X-rays" subtitle="Upload reports, X-rays and any patient paperwork."/>
              <DocumentVault key={selectedPatient._id} patientId={selectedPatient._id} API_BASE={API_BASE} UPLOAD_BASE={UPLOAD_BASE} triggerUpload={uploadDocTrigger}/>
            </div>
          )}

          {activeKey === 'notes' && (
            <div style={S.paneCard}>
              <PaneHeader title="Clinical notes" subtitle="Internal observations and longitudinal notes."/>
              <NotesSection key={selectedPatient._id} patientId={selectedPatient._id} API_BASE={API_BASE}/>
            </div>
          )}

          {activeKey === 'comms' && (
            <div style={S.paneCard}>
              <PaneHeader title="Communications" subtitle="Calls, voicemails, WhatsApp threads — all together."/>
              <PatientCommsPane patientId={selectedPatient._id} patientPhone={selectedPatient.phone} API_BASE={API_BASE}/>
            </div>
          )}

          {/* CLINICAL main tab — sub-tab strip + selected sub-tab content */}
          {activeKey === 'clinical' && (
            <div style={S.paneCard}>
              <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
                {CLINICAL_SUB_TABS.map(s => (
                  <button key={s.key} onClick={() => setClinicalSub(s.key)}
                    style={{
                      padding: '7px 14px', borderRadius: 6, border: 'none', cursor: 'pointer',
                      fontWeight: 600, fontSize: '0.82rem',
                      backgroundColor: clinicalSub === s.key ? '#0582ca' : '#e9ecef',
                      color: clinicalSub === s.key ? 'white' : '#555',
                    }}>
                    {s.label}
                  </button>
                ))}
              </div>
              {clinicalSub === 'imaging' && (
                <ImagingHubPage API_BASE={API_BASE} lockedPatientId={selectedPatient._id} />
              )}
              {CLINICAL_TABS_MAP[clinicalSub] && (
                <ClinicalPage
                  API_BASE={API_BASE}
                  currentUser={app.currentUser}
                  initialTab={CLINICAL_TABS_MAP[clinicalSub]}
                  lockedPatient={selectedPatient}
                />
              )}
            </div>
          )}

          {/* VITALS — moved out of Clinical sub-tabs to sidebar quick-access */}
          {activeKey === 'vitals' && (
            <div style={S.paneCard}>
              <VitalSignsPage API_BASE={API_BASE} lockedPatientId={selectedPatient._id} />
            </div>
          )}

          {activeKey === 'pharmacy' && (
            <div style={S.paneCard}>
              <PaneHeader title="Pharmacy" subtitle="Dispensed medicines, lifetime spend, outstanding credit."/>
              <PatientPharmacyPane patientId={selectedPatient._id} API_BASE={API_BASE}/>
            </div>
          )}

          {activeKey === 'lab-work' && (
            <div style={S.paneCard}>
              <PaneHeader title="Lab Work" subtitle="Crown/bridge/denture cases sent to external labs, with live status." />
              <PatientLabWorkPane patientId={selectedPatient._id} API_BASE={API_BASE} />
            </div>
          )}

          {activeKey === 'calls' && (
            <div style={S.paneCard}>
              <PaneHeader title="Call history" subtitle="Every incoming and outgoing call linked to this patient."/>
              <PatientCallHistory patientId={selectedPatient._id} API_BASE={API_BASE} />
            </div>
          )}

          {activeKey === 'feedback' && (
            <div style={S.paneCard}>
              <PaneHeader title="Patient feedback" subtitle="Post-visit satisfaction and reviews."/>
              <QuickFeedback key={selectedPatient._id} patientId={selectedPatient._id} API_BASE={API_BASE}/>
            </div>
          )}

          {activeKey === 'forms' && (
            <FormsPane
              S={S}
              clinic={clinic}
              selectedPatient={selectedPatient}
              patientConsents={patientConsents}
              setShowRegForm={setShowRegForm}
            />
          )}
        </main>
      </div>

      {/* ═══ Modals ═══ */}
      {printModeModal && (
        <div style={S.modalBackdrop} onClick={() => { printModeModal('plain'); setPrintModeModal(null); }}>
          <div style={S.modalCard} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 6px', color: theme.navy }}>Print mode</h3>
            <p style={{ color: theme.textMuted, fontSize: 13, margin: '0 0 20px' }}>Choose paper type</p>
            <div style={{ display: 'flex', gap: 14 }}>
              <button onClick={() => { printModeModal('plain'); setPrintModeModal(null); }}
                style={S.printModeBtnA}>
                <div style={{ fontSize: 28, marginBottom: 6 }}>📄</div>
                Plain paper
                <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 4 }}>Header will be printed</div>
              </button>
              <button onClick={() => { printModeModal('letterhead'); setPrintModeModal(null); }}
                style={S.printModeBtnB}>
                <div style={{ fontSize: 28, marginBottom: 6 }}>📋</div>
                Letterhead
                <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 4 }}>Header left blank</div>
              </button>
            </div>
          </div>
        </div>
      )}

      {showFaceReg && (
        <FaceRegister
          API_BASE={API_BASE}
          patientId={selectedPatient._id}
          patientName={fullName}
          onDone={() => setShowFaceReg(false)}
          onClose={() => setShowFaceReg(false)}
        />
      )}

      {showSmartRecord && (
        <SmartRecordModal
          API_BASE={API_BASE}
          patientId={selectedPatient._id}
          patientName={fullName}
          treatmentTypes={treatmentTypes}
          onClose={() => setShowSmartRecord(false)}
        />
      )}

      {editMode && (
        <EditModal
          editForm={editForm} setEditForm={setEditForm}
          saveEdit={saveEdit} close={() => setEditMode(false)}
          selectedPatient={selectedPatient}
          medicalList={medicalList} dentalList={dentalList}
          allergiesList={allergiesList} groupsList={groupsList} tagsList={tagsList}
        />
      )}

      {/* ═══ Payments drawer (slides in from right) ═══ */}
      {paymentOpen && (
        <div style={S.drawerBackdrop} onClick={() => setPaymentOpen(false)}>
          <div style={S.paymentDrawer} onClick={e => e.stopPropagation()}>
            <div style={S.drawerHead}>
              <div>
                <div style={S.drawerTitle}>Payments & bills</div>
                <div style={S.drawerSub}>
                  {pastBills.length} bills · {hasDueBalance ? '₹' + (ptOutstanding.outstanding || 0).toLocaleString('en-IN') + ' due' : 'Fully paid'}
                </div>
              </div>
              <button onClick={() => setPaymentOpen(false)} style={S.drawerClose}><X size={18}/></button>
            </div>
            <div style={{ padding: 20, overflowY: 'auto', flex: 1 }}>
              <PaymentSection
                key={selectedPatient._id}
                patientId={selectedPatient._id}
                API_BASE={API_BASE}
                patientName={fullName}
                patientPhone={selectedPatient.phone}
                patientUhid={selectedPatient.uhid}
                onPaymentSuccess={(amount, mode, voucherNo, outstanding) => whatsappPaymentReceived(selectedPatient, amount, mode, voucherNo, outstanding)}
              />
            </div>
          </div>
        </div>
      )}

      {/* ═══ Floating Action Drawer — right edge, always visible ═══ */}
      <FloatingActionDrawer
        open={fabOpen}
        setOpen={setFabOpen}
        hasPhone={!!selectedPatient.phone}
        hasBalance={hasDueBalance}
        actions={{
          newVisit:       () => { goTab('visits'); setNewVisitTrigger(n => n + 1); setFabOpen(false); },
          // Write Rx and New bill both open a new visit form (Rx and Bill
          // are sections inside a visit). Without incrementing
          // newVisitTrigger the form never opens for a virgin patient,
          // so the action becomes a no-op (real bug found by Playwright).
          writeRx:        () => { goTab('visits'); setNewVisitTrigger(n => n + 1); setIsWritingPrescription(true); setFabOpen(false); },
          newBill:        () => { goTab('visits'); setNewVisitTrigger(n => n + 1); setIsWritingBill(true); setFabOpen(false); },
          // Goto Documents tab AND auto-open the upload modal (UX:
          // user clicked "Upload doc" expecting an immediate file picker,
          // not just a tab switch).
          uploadDoc:      () => { goTab('documents'); setUploadDocTrigger(n => n + 1); setFabOpen(false); },
          smartRecord:    () => { setShowSmartRecord(true); setFabOpen(false); },
          registerFace:   () => { setShowFaceReg(true); setFabOpen(false); },
          recordPayment:  () => { setPaymentOpen(true); setFabOpen(false); },
          editPatient:    () => { startEdit(); setFabOpen(false); },
          deletePatient:  () => { handleDelete(); setFabOpen(false); },
          whatsapp: {
            summary:        () => { whatsappPatientSummary(selectedPatient, pastTreatments, pastBills); setFabOpen(false); },
            treatmentDone:  () => { const d = doneTx[0]; if (d) whatsappTreatmentDone(selectedPatient, d.treatmentName, d.toothNumber, [], [], d.totalAmount); else alert('No completed treatments yet'); setFabOpen(false); },
            paymentReceived:() => { whatsappPaymentReceived(selectedPatient, ptOutstanding?.paid || 0, 'Cash', '-', ptOutstanding?.outstanding || 0); setFabOpen(false); },
            treatmentPlan:  () => { const p = plannedTx[0]; if (p) whatsappTreatmentPlan(selectedPatient, p.treatmentName, p.toothNumber, p.totalAmount); else alert('No pending plans'); setFabOpen(false); },
          },
          print: {
            card:        () => { printPatientCard(selectedPatient); setFabOpen(false); },
            caseSheet:   () => { handlePrintCaseSheet(); setFabOpen(false); },
            discharge:   () => { handlePrintDischarge(doneTx[0]); setFabOpen(false); },
            registration:() => { setShowRegForm(selectedPatient); setFabOpen(false); },
          },
        }}
      />
    </div>
  );
};

/* ═══════════════════════ SUB-COMPONENTS ═══════════════════════ */

/* ─── Floating Action Drawer — right-edge speed-dial ─── */

const FloatingActionDrawer = ({ open, setOpen, hasPhone, hasBalance, actions }) => {
  const [sub, setSub] = React.useState(null); // 'whatsapp' | 'print' | null
  React.useEffect(() => {
    if (!open) setSub(null);
  }, [open]);

  const itemsCreate = [
    { icon: Plus,     label: 'New visit',      onClick: actions.newVisit,      accent: '#F57C1F' },
    { icon: FileText, label: 'Write Rx',       onClick: actions.writeRx },
    { icon: Receipt,  label: 'New bill',       onClick: actions.newBill },
    { icon: CreditCard, label: hasBalance ? 'Record payment' : 'Payments',   onClick: actions.recordPayment, accent: hasBalance ? '#dc2626' : undefined },
    { icon: Upload,   label: 'Upload doc',     onClick: actions.uploadDoc },
  ];
  const itemsCapture = [
    { icon: Mic,      label: 'Smart record',   onClick: actions.smartRecord },
    { icon: Camera,   label: 'Register face',  onClick: actions.registerFace },
  ];
  const waSub = [
    { label: 'Summary',            onClick: actions.whatsapp.summary },
    { label: 'Treatment done',     onClick: actions.whatsapp.treatmentDone },
    { label: 'Payment received',   onClick: actions.whatsapp.paymentReceived },
    { label: 'Treatment plan',     onClick: actions.whatsapp.treatmentPlan },
  ];
  const printSub = [
    { label: 'Patient card',       onClick: actions.print.card },
    { label: 'Case sheet',         onClick: actions.print.caseSheet },
    { label: 'Discharge summary',  onClick: actions.print.discharge },
    { label: 'Registration form',  onClick: actions.print.registration },
  ];

  return (
    <>
      {/* The FAB — anchored top-right below the header/date (clears Veeransh AI at bottom-right) */}
      <button
        onClick={() => setOpen(o => !o)}
        title="Quick actions"
        style={{
          position: 'fixed',
          right: open ? 316 : 24,
          top: 100,
          zIndex: 90,
          height: 44, padding: open ? '0 14px' : '0 18px',
          borderRadius: 999,
          background: open
            ? theme.surface
            : `linear-gradient(135deg, ${theme.primary} 0%, ${theme.secondary} 100%)`,
          color: open ? theme.navy : 'white',
          border: open ? `1px solid ${theme.border}` : 'none',
          cursor: 'pointer',
          display: 'inline-flex', alignItems: 'center', gap: 8,
          fontSize: 13.5, fontWeight: 700, letterSpacing: 0.2,
          fontFamily: 'inherit',
          boxShadow: open
            ? '0 6px 16px rgba(15,23,42,0.15)'
            : '0 10px 28px rgba(245,124,31,0.4)',
          transition: 'right 0.28s cubic-bezier(.2,.8,.2,1), background 0.2s, box-shadow 0.2s',
        }}>
        {open
          ? <><X size={16}/> Close</>
          : <><Plus size={18} strokeWidth={2.6}/> New visit</>}
      </button>

      {/* Backdrop */}
      {open && (
        <div
          onClick={() => setOpen(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 80,
            background: 'rgba(10,15,30,0.08)',
            backdropFilter: 'blur(2px)',
          }}/>
      )}

      {/* The Drawer */}
      <aside
        style={{
          position: 'fixed',
          top: 0, right: 0, bottom: 0,
          width: 300, zIndex: 85,
          background: theme.surface,
          borderLeft: `1px solid ${theme.border}`,
          boxShadow: '0 0 40px rgba(15,23,42,0.15)',
          transform: open ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.3s cubic-bezier(.2,.8,.2,1)',
          display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
        }}>
        <div style={{ padding: '18px 20px', borderBottom: `1px solid ${theme.borderLight}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: theme.navy, letterSpacing: '-0.01em' }}>
              {sub === 'whatsapp' ? 'WhatsApp' : sub === 'print' ? 'Print' : 'Quick actions'}
            </div>
            {!sub && <div style={{ fontSize: 11.5, color: theme.textMuted, marginTop: 2 }}>Tap anything to run.</div>}
          </div>
          {sub ? (
            <button onClick={() => setSub(null)} style={{ background: 'transparent', border: 'none', color: theme.textMuted, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>← Back</button>
          ) : null}
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: 14 }}>
          {sub === null && (
            <>
              <DrawerGroup title="Record">
                {itemsCreate.map(a => <DrawerItem key={a.label} {...a}/>)}
              </DrawerGroup>

              <DrawerGroup title="Capture">
                {itemsCapture.map(a => <DrawerItem key={a.label} {...a}/>)}
              </DrawerGroup>

              <DrawerGroup title="Share & print">
                <DrawerItem
                  icon={MessageCircle}
                  label="WhatsApp"
                  accent="#25D366"
                  disabled={!hasPhone}
                  chevron
                  onClick={() => hasPhone && setSub('whatsapp')}
                />
                <DrawerItem
                  icon={Printer}
                  label="Print"
                  chevron
                  onClick={() => setSub('print')}
                />
              </DrawerGroup>

              <DrawerGroup title="Manage">
                <DrawerItem icon={Edit3} label="Edit patient" onClick={actions.editPatient}/>
                <DrawerItem icon={Trash2} label="Delete patient" accent={theme.danger} onClick={actions.deletePatient}/>
              </DrawerGroup>
            </>
          )}

          {sub === 'whatsapp' && (
            <div>
              {waSub.map(o => (
                <button key={o.label} onClick={o.onClick} style={drawerSubBtnStyle()}>
                  <MessageCircle size={14} color="#25D366"/>
                  <span>{o.label}</span>
                </button>
              ))}
            </div>
          )}
          {sub === 'print' && (
            <div>
              {printSub.map(o => (
                <button key={o.label} onClick={o.onClick} style={drawerSubBtnStyle()}>
                  <Printer size={14} color={theme.navy}/>
                  <span>{o.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </aside>
    </>
  );
};

const DrawerGroup = ({ title, children }) => (
  <div style={{ marginBottom: 10 }}>
    <div style={{ fontSize: 10.5, fontWeight: 700, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: 1.2, padding: '6px 4px 6px' }}>
      {title}
    </div>
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>{children}</div>
  </div>
);

const DrawerItem = ({ icon: Icon, label, onClick, accent, disabled, chevron }) => (
  <button
    onClick={() => !disabled && onClick?.()}
    disabled={disabled}
    style={{
      display: 'flex', alignItems: 'center', gap: 10,
      padding: '10px 12px', borderRadius: 10,
      background: 'transparent', border: '1px solid transparent',
      color: disabled ? theme.textSubtle : (accent || theme.navy),
      fontSize: 13.5, fontWeight: 600, cursor: disabled ? 'not-allowed' : 'pointer',
      textAlign: 'left', width: '100%', fontFamily: 'inherit',
      opacity: disabled ? 0.5 : 1,
      transition: 'all 0.15s',
    }}
    onMouseEnter={e => { if (!disabled) { e.currentTarget.style.background = theme.bg; e.currentTarget.style.borderColor = theme.border; } }}
    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'transparent'; }}>
    <span style={{
      width: 32, height: 32, borderRadius: 9,
      background: accent ? `${accent}18` : theme.bg,
      color: accent || theme.primary,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      border: `1px solid ${accent ? `${accent}35` : theme.border}`,
      flexShrink: 0,
    }}>
      <Icon size={15}/>
    </span>
    <span style={{ flex: 1 }}>{label}</span>
    {chevron && <ChevronDown size={14} style={{ transform: 'rotate(-90deg)', color: theme.textSubtle }}/>}
  </button>
);

const drawerSubBtnStyle = () => ({
  display: 'flex', alignItems: 'center', gap: 10,
  padding: '11px 14px', width: '100%',
  background: 'transparent', border: `1px solid ${theme.borderLight}`,
  color: theme.navy, fontSize: 13.5, fontWeight: 500,
  cursor: 'pointer', borderRadius: 10, marginBottom: 6,
  textAlign: 'left', fontFamily: 'inherit',
});

const SidebarSection = ({ title, icon: Icon, children }) => (
  <div style={{ padding: '14px 16px', borderTop: `1px solid ${theme.borderLight}` }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 700, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>
      {Icon && <Icon size={12}/>}
      {title}
    </div>
    {children}
  </div>
);

/* QABtn and MenuBtn removed — actions now live in <FloatingActionDrawer/> */

const PaneHeader = ({ title, subtitle, right }) => (
  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 18, paddingBottom: 14, borderBottom: `1px solid ${theme.borderLight}` }}>
    <div>
      <h2 style={{ margin: 0, color: theme.navy, fontSize: 20, fontWeight: 700, letterSpacing: '-0.01em' }}>{title}</h2>
      {subtitle && <div style={{ marginTop: 4, fontSize: 13, color: theme.textMuted }}>{subtitle}</div>}
    </div>
    {right && <div>{right}</div>}
  </div>
);

const EmptyState = ({ icon: Icon, message, cta, onClick }) => (
  <div style={{ textAlign: 'center', padding: '50px 20px', color: theme.textMuted }}>
    {Icon && <Icon size={36} style={{ margin: '0 auto 10px', opacity: 0.4 }}/>}
    <div style={{ fontSize: 14, marginBottom: 14 }}>{message}</div>
    {cta && <button onClick={onClick} style={{ padding: '9px 20px', background: theme.primary, color: 'white', border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>{cta}</button>}
  </div>
);

const RxCard = ({ rx, onPrint, onDelete }) => (
  <div style={{ background: theme.bg, borderRadius: 10, padding: '14px 16px', border: `1px solid ${theme.border}` }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <Clock size={13} color={theme.textMuted}/>
        <strong style={{ color: theme.navy, fontSize: 14 }}>
          {new Date(rx.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
        </strong>
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        <button onClick={onPrint} style={{ padding: '5px 12px', background: theme.primary, color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <Printer size={12}/> Print
        </button>
        <button onClick={onDelete} style={{ padding: '5px 10px', background: 'white', color: theme.danger, border: `1px solid ${theme.border}`, borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
          <Trash2 size={12}/>
        </button>
      </div>
    </div>
    {rx.clinicalNotes?.length > 0 && (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 8 }}>
        {rx.clinicalNotes.map((cn, i) => (
          <span key={i} style={{ background: '#e0f2fe', color: '#0369a1', padding: '2px 8px', borderRadius: 12, fontSize: 11.5, fontWeight: 600 }}>
            #{cn.toothNumber}{cn.surfaces?.length ? ` [${cn.surfaces.join(',')}]` : ''}
          </span>
        ))}
      </div>
    )}
    {rx.chiefComplaint && <RxLine label="CC" value={rx.chiefComplaint}/>}
    {rx.findings && <RxLine label="Findings" value={rx.findings}/>}
    {rx.diagnosis && <RxLine label="Dx" value={rx.diagnosis}/>}
    {rx.medicines?.length > 0 && (
      <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px dashed ${theme.border}` }}>
        {rx.medicines.map((m, i) => (
          <div key={i} style={{ fontSize: 13, color: theme.navy, marginBottom: 2 }}>
            <Pill size={11} style={{ display: 'inline-block', marginRight: 5, verticalAlign: '-1px', color: theme.secondary }}/>
            <strong>{m.name}</strong>
            {m.dosage && <span style={{ color: theme.textMuted }}> · {m.dosage}</span>}
            {m.duration && <span style={{ color: theme.textMuted }}> · {m.duration}</span>}
          </div>
        ))}
      </div>
    )}
    {rx.notes && <div style={{ marginTop: 8, fontSize: 12.5, color: theme.textMuted, fontStyle: 'italic' }}>“{rx.notes}”</div>}
  </div>
);

const RxLine = ({ label, value }) => (
  <div style={{ fontSize: 13, color: theme.navy, marginBottom: 3 }}>
    <span style={{ color: theme.textMuted, fontWeight: 600 }}>{label}:</span> {value}
  </div>
);

/* ─── OVERVIEW PANE ─── */

const OverviewPane = ({ S, selectedPatient, ptOutstanding, pastTreatments, plannedTx, doneTx, pastPrescriptions, pastBills, handlePrintRx, goTab, openPayments }) => {
  const kpis = [
    { label: 'Billed',         value: `₹${(ptOutstanding?.billed || 0).toLocaleString('en-IN')}`,         color: theme.navy, icon: Receipt },
    { label: 'Paid',           value: `₹${(ptOutstanding?.paid || 0).toLocaleString('en-IN')}`,           color: theme.success, icon: CreditCard },
    { label: 'Outstanding',    value: `₹${(ptOutstanding?.outstanding || 0).toLocaleString('en-IN')}`,    color: ptOutstanding?.outstanding > 0 ? theme.danger : theme.success, icon: AlertTriangle },
    { label: 'Treatments',     value: pastTreatments.length,                                               color: theme.navy, icon: Stethoscope },
    { label: 'Prescriptions',  value: pastPrescriptions.length,                                            color: theme.navy, icon: FileText },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* KPI strip */}
      <div style={S.kpiGrid}>
        {kpis.map(k => {
          const Icon = k.icon;
          return (
            <div key={k.label} style={S.kpiCard}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={S.kpiLabel}>{k.label}</span>
                <Icon size={14} color={theme.textSubtle}/>
              </div>
              <div style={{ ...S.kpiValue, color: k.color }}>{k.value}</div>
            </div>
          );
        })}
      </div>

      {/* ─── Accordion-style stack (one below the other) ─── */}
      <AccordionSection
        title="Treatment plan"
        count={plannedTx.length}
        accent={theme.warning}
        icon={Stethoscope}
        defaultOpen
        action={{ label: 'Open visits →', onClick: () => goTab('visits') }}
      >
        {plannedTx.length === 0 ? (
          <EmptyState icon={Stethoscope} message="No pending treatments planned."/>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {plannedTx.map(t => (
              <div key={t._id} style={S.planRow}>
                <div>
                  <div style={{ fontWeight: 600, color: theme.navy, fontSize: 14 }}>{t.treatmentName}</div>
                  <div style={{ fontSize: 12, color: theme.textMuted, marginTop: 2 }}>
                    {t.toothNumber ? `Tooth #${t.toothNumber} · ` : ''}
                    {t.plannedDate ? new Date(t.plannedDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : ''}
                  </div>
                </div>
                <div style={{ fontWeight: 700, color: theme.warning, fontSize: 14 }}>₹{(t.totalAmount || 0).toLocaleString('en-IN')}</div>
              </div>
            ))}
          </div>
        )}
      </AccordionSection>

      <AccordionSection
        title="Completed"
        count={doneTx.length}
        accent={theme.success}
        icon={Stethoscope}
      >
        {doneTx.length === 0 ? (
          <EmptyState icon={Stethoscope} message="No completed treatments yet."/>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {doneTx.slice(0, 8).map(t => (
              <div key={t._id} style={{ ...S.planRow, background: '#f0fdf4', borderColor: '#bbf7d0' }}>
                <div>
                  <div style={{ fontWeight: 600, color: theme.navy, fontSize: 14 }}>{t.treatmentName}</div>
                  <div style={{ fontSize: 12, color: theme.textMuted, marginTop: 2 }}>
                    {t.toothNumber ? `Tooth #${t.toothNumber} · ` : ''}
                    {t.doneDate ? new Date(t.doneDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : ''}
                  </div>
                </div>
                <div style={{ fontWeight: 700, color: theme.success, fontSize: 14 }}>₹{(t.totalAmount || 0).toLocaleString('en-IN')}</div>
              </div>
            ))}
            {doneTx.length > 8 && (
              <div style={{ fontSize: 12, color: theme.textMuted, textAlign: 'center', paddingTop: 6 }}>+{doneTx.length - 8} more</div>
            )}
          </div>
        )}
      </AccordionSection>

      <AccordionSection
        title="Prescriptions"
        count={pastPrescriptions.length}
        accent={theme.primary}
        icon={FileText}
        action={{ label: 'View in visits →', onClick: () => goTab('visits') }}
      >
        {pastPrescriptions.filter(rx => (rx.medicines || []).length > 0).length === 0 ? (
          <EmptyState icon={FileText} message="No prescriptions yet."/>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {pastPrescriptions.filter(rx => (rx.medicines || []).length > 0).slice(0, 5).map(rx => (
              <div key={rx._id} style={S.miniCard}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <strong style={{ color: theme.navy, fontSize: 13 }}>
                    {new Date(rx.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </strong>
                  <button onClick={() => handlePrintRx(rx)} style={{ padding: '3px 10px', background: theme.primary, color: 'white', border: 'none', borderRadius: 5, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>Print</button>
                </div>
                {rx.medicines.slice(0, 4).map((m, i) => (
                  <div key={i} style={{ fontSize: 12.5, color: theme.textMuted, marginTop: 2 }}>
                    <Pill size={11} style={{ display: 'inline-block', marginRight: 5, verticalAlign: '-1px', color: theme.secondary }}/>
                    <strong style={{ color: theme.navy }}>{m.name}</strong>
                    {m.dosage ? ` · ${m.dosage}` : ''}
                    {m.duration ? ` · ${m.duration}` : ''}
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </AccordionSection>

      <AccordionSection
        title="Bills"
        count={pastBills.length}
        accent={theme.secondary}
        icon={Receipt}
        action={{ label: 'Payments →', onClick: openPayments }}
      >
        {pastBills.length === 0 ? (
          <EmptyState icon={Receipt} message="No bills raised yet."/>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {pastBills.slice(0, 8).map(b => (
              <div key={b._id} style={{ ...S.miniCard, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <strong style={{ color: theme.navy, fontSize: 13 }}>{b.invoiceNo || new Date(b.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</strong>
                  <div style={{ fontSize: 11.5, color: theme.textMuted, marginTop: 2 }}>{(b.items || []).map(i => i.description).join(', ').substring(0, 60)}</div>
                </div>
                <div style={{ fontWeight: 700, color: theme.success, fontSize: 14 }}>₹{(b.totalAmount || 0).toLocaleString('en-IN')}</div>
              </div>
            ))}
          </div>
        )}
      </AccordionSection>
    </div>
  );
};

/* ─── Accordion row used by OverviewPane ─── */

const AccordionSection = ({ title, count, accent, icon: Icon, defaultOpen = false, action, children }) => {
  const [open, setOpen] = React.useState(defaultOpen);
  return (
    <div style={{
      background: theme.surface,
      borderRadius: 12,
      border: `1px solid ${theme.border}`,
      boxShadow: theme.shadowSm,
      overflow: 'hidden',
    }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 12,
          padding: '14px 18px', background: 'transparent', border: 'none',
          cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left',
        }}>
        <span style={{
          width: 32, height: 32, borderRadius: 9,
          background: `${accent}18`, color: accent,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          border: `1px solid ${accent}30`, flexShrink: 0,
        }}>
          <Icon size={15}/>
        </span>
        <span style={{ flex: 1, fontSize: 15, fontWeight: 700, color: theme.navy }}>
          {title}
          <span style={{
            marginLeft: 8, padding: '1px 9px', borderRadius: 999,
            background: theme.bg, color: theme.textMuted,
            fontSize: 11.5, fontWeight: 600,
          }}>{count}</span>
        </span>
        {action && open && (
          <span
            onClick={(e) => { e.stopPropagation(); action.onClick(); }}
            style={{
              fontSize: 12, color: theme.primaryDark, fontWeight: 600,
              padding: '4px 10px', borderRadius: 6, cursor: 'pointer',
            }}>
            {action.label}
          </span>
        )}
        <ChevronDown
          size={18}
          color={theme.textMuted}
          style={{
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.25s cubic-bezier(.2,.8,.2,1)',
          }}
        />
      </button>
      <div style={{
        maxHeight: open ? 2000 : 0,
        overflow: 'hidden',
        transition: 'max-height 0.35s cubic-bezier(.2,.8,.2,1)',
      }}>
        <div style={{ padding: '4px 18px 18px' }}>{children}</div>
      </div>
    </div>
  );
};

/* ─── PHARMACY PANE (Batch 4a) ─── */
// Shows the dispense history for a single patient. Data source:
// GET /pharmacy/sales/patient/:id — returns { sales[], totalSpent,
// totalDispensed, outstanding }. Each row shows the bill number, dispense
// date, medicine summary, and the source Rx link when available.

function PatientPharmacyPane({ patientId, API_BASE }) {
  const [data, setData] = React.useState({ sales: [], totalSpent: 0, totalDispensed: 0, outstanding: 0 });
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    if (!patientId) return;
    setLoading(true);
    const headers = (() => {
      const t = sessionStorage.getItem('token') || localStorage.getItem('token');
      return t ? { Authorization: `Bearer ${t}` } : {};
    })();
    fetch(`${API_BASE}/pharmacy/sales/patient/${patientId}`, { headers })
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [patientId, API_BASE]);

  if (loading) return <div style={{ padding: 16, color: '#6b7280' }}>Loading pharmacy history…</div>;
  if (error) return <div style={{ padding: 16, color: '#dc2626' }}>Failed to load: {error}</div>;
  if (!data.sales.length) return <div style={{ padding: 16, color: '#6b7280' }}>No pharmacy dispenses yet for this patient.</div>;

  const fmtINR = (n) => `₹${Math.round(Number(n) || 0).toLocaleString('en-IN')}`;
  const fmtDate = (d) => new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Summary strip — three KPI cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
        <div style={{ padding: 12, backgroundColor: '#f0fdf4', borderRadius: 8, borderLeft: '4px solid #16a34a' }}>
          <div style={{ fontSize: 11, color: '#065f46', fontWeight: 700, textTransform: 'uppercase' }}>Lifetime spend</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: '#065f46', marginTop: 4 }}>{fmtINR(data.totalSpent)}</div>
        </div>
        <div style={{ padding: 12, backgroundColor: '#eff6ff', borderRadius: 8, borderLeft: '4px solid #3b82f6' }}>
          <div style={{ fontSize: 11, color: '#1e40af', fontWeight: 700, textTransform: 'uppercase' }}>Dispenses</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: '#1e40af', marginTop: 4 }}>{data.totalDispensed}</div>
        </div>
        <div style={{ padding: 12, backgroundColor: data.outstanding > 0 ? '#fef2f2' : '#f3f4f6', borderRadius: 8, borderLeft: `4px solid ${data.outstanding > 0 ? '#dc2626' : '#9ca3af'}` }}>
          <div style={{ fontSize: 11, color: data.outstanding > 0 ? '#991b1b' : '#374151', fontWeight: 700, textTransform: 'uppercase' }}>Outstanding</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: data.outstanding > 0 ? '#991b1b' : '#374151', marginTop: 4 }}>{fmtINR(data.outstanding)}</div>
        </div>
      </div>

      {/* Sale rows */}
      {data.sales.map((s) => (
        <div key={s._id} style={{ padding: 12, border: '1px solid #e5e7eb', borderRadius: 8, backgroundColor: 'white' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
            <div>
              <div style={{ fontWeight: 700, color: '#111827' }}>{s.billNo || '—'}</div>
              <div style={{ fontSize: 12, color: '#6b7280' }}>{fmtDate(s.createdAt)} · {s.paymentMode || 'cash'} · {s.doctorName || '—'}</div>
              {s.prescriptionId && (
                <div style={{ fontSize: 11, color: '#6366f1', marginTop: 2 }}>
                  🗒 From Rx {s.prescriptionId.date ? fmtDate(s.prescriptionId.date) : ''}
                  {s.prescriptionId.chiefComplaint ? ` · ${s.prescriptionId.chiefComplaint}` : ''}
                </div>
              )}
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontWeight: 800, fontSize: 16, color: '#111827' }}>{fmtINR(s.totalAmount)}</div>
              <span style={{ display: 'inline-block', padding: '2px 8px', backgroundColor: s.status === 'completed' ? '#d1fae5' : '#fef3c7', color: s.status === 'completed' ? '#065f46' : '#92400e', borderRadius: 10, fontSize: 10, fontWeight: 700, marginTop: 4 }}>{s.status}</span>
            </div>
          </div>
          {/* Item list */}
          {Array.isArray(s.items) && s.items.length > 0 && (
            <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px dashed #e5e7eb' }}>
              {s.items.map((it, i) => (
                <div key={i} style={{ fontSize: 12, color: '#374151', display: 'flex', justifyContent: 'space-between' }}>
                  <span>💊 {it.itemName} × {it.quantity}{it.dosage ? ` (${it.dosage}${it.duration ? ', ' + it.duration : ''})` : ''}</span>
                  <span style={{ color: '#6b7280' }}>{fmtINR(it.amount)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/* ─── LAB WORK PANE ─── */

function PatientLabWorkPane({ patientId, API_BASE }) {
  const [orders, setOrders] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  const load = React.useCallback(() => {
    if (!patientId) return;
    setLoading(true);
    const t = sessionStorage.getItem('token') || localStorage.getItem('token');
    const headers = t ? { Authorization: `Bearer ${t}` } : {};
    fetch(`${API_BASE}/lab/orders/patient/${patientId}`, { headers })
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then(setOrders)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [patientId, API_BASE]);

  React.useEffect(() => { load(); }, [load]);

  if (loading) return <div style={{ padding: 16, color: '#6b7280' }}>Loading lab cases…</div>;
  if (error) return <div style={{ padding: 16, color: '#dc2626' }}>Failed to load: {error}</div>;
  if (!orders.length) return <div style={{ padding: 16, color: '#6b7280' }}>No lab cases for this patient yet. Lab cases auto-create when a treatment marked "done" needs lab work.</div>;

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
  const fmtTime = (d) => d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';
  const fmtINR = (n) => `₹${Math.round(Number(n) || 0).toLocaleString('en-IN')}`;

  const statusStyles = {
    pending:       { bg: '#fee2e2', color: '#991b1b', label: 'Pending Dispatch' },
    sent:          { bg: '#fef3c7', color: '#92400e', label: 'Sent to Lab' },
    'in-progress': { bg: '#dbeafe', color: '#1e40af', label: 'In Progress' },
    received:      { bg: '#d1fae5', color: '#065f46', label: 'Received' },
    delivered:     { bg: '#e5e7eb', color: '#374151', label: 'Delivered to Patient' },
    cancelled:     { bg: '#fee2e2', color: '#991b1b', label: 'Cancelled' },
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {orders.map(o => {
        const ss = statusStyles[o.status] || statusStyles.sent;
        const replies = Array.isArray(o.labReplyLog) ? o.labReplyLog : [];
        return (
          <div key={o._id} style={{ padding: 14, border: '1px solid #e5e7eb', borderRadius: 8, backgroundColor: 'white' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                  {o.caseCode && <span style={{ fontFamily: 'monospace', fontWeight: 700, color: '#1e40af', fontSize: 13 }}>{o.caseCode}</span>}
                  <strong style={{ color: '#111827' }}>{o.items || 'Lab case'}</strong>
                  {o.shade && <span style={{ fontSize: 12, color: '#6b7280' }}>· Shade {o.shade}</span>}
                </div>
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                  Lab: <strong>{o.vendorId?.name || 'Unassigned'}</strong>
                  {o.cost > 0 && <> · Cost {fmtINR(o.cost)}</>}
                </div>
              </div>
              <span style={{ padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700, backgroundColor: ss.bg, color: ss.color, whiteSpace: 'nowrap' }}>
                {ss.label}
              </span>
            </div>

            {/* Timeline — Sent → Received → Delivered */}
            <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
              <TimelineStep label="Sent" date={o.dispatchedAt || o.sentDate} />
              <TimelineStep label="Lab Notified" date={o.labNotifiedAt} sub="📱 WhatsApp" />
              <TimelineStep label="Received" date={o.receivedDate} />
              <TimelineStep label="Delivered" date={o.deliveredDate} />
            </div>

            {replies.length > 0 && (
              <details style={{ marginTop: 10, paddingTop: 10, borderTop: '1px dashed #e5e7eb' }}>
                <summary style={{ cursor: 'pointer', fontSize: 12, color: '#6366f1', fontWeight: 600 }}>
                  {replies.length} WhatsApp repl{replies.length === 1 ? 'y' : 'ies'} from lab
                </summary>
                <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {replies.map((r, i) => (
                    <div key={i} style={{ fontSize: 12, padding: 6, backgroundColor: '#f9fafb', borderRadius: 6 }}>
                      <span style={{ color: '#065f46', fontWeight: 600 }}>{r.matchedStatus}</span>
                      <span style={{ color: '#6b7280' }}> · {fmtTime(r.at)}</span>
                      <div style={{ color: '#374151' }}>"{r.text}"</div>
                    </div>
                  ))}
                </div>
              </details>
            )}
          </div>
        );
      })}
    </div>
  );
}

const TimelineStep = ({ label, date, sub }) => (
  <div style={{ padding: 8, backgroundColor: date ? '#f0fdf4' : '#f9fafb', borderRadius: 6, borderLeft: `3px solid ${date ? '#16a34a' : '#d1d5db'}` }}>
    <div style={{ fontSize: 10, fontWeight: 700, color: date ? '#065f46' : '#9ca3af', textTransform: 'uppercase' }}>{label}</div>
    <div style={{ fontSize: 12, color: date ? '#111827' : '#9ca3af', marginTop: 2 }}>
      {date ? new Date(date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '—'}
    </div>
    {sub && date && <div style={{ fontSize: 10, color: '#6b7280', marginTop: 1 }}>{sub}</div>}
  </div>
);

/* ─── FORMS PANE ─── */

const FormsPane = ({ S, clinic, selectedPatient, patientConsents, setShowRegForm }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
    <div style={S.paneCard}>
      <PaneHeader
        title="Registration form"
        subtitle="Auto-filled from patient record — print for signing."
        right={
          <button onClick={() => setShowRegForm(selectedPatient)} style={S.primaryBtn}>
            <Printer size={14}/> Print form
          </button>
        }
      />
    </div>

    <div style={S.paneCard}>
      <PaneHeader title="Consent forms" subtitle="Auto-generated after each treatment."/>
      {patientConsents.length === 0 ? (
        <EmptyState icon={ClipboardList} message="No consent forms yet. They generate when a treatment is marked done."/>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {patientConsents.map(c => (
            <div key={c._id} style={{ background: theme.bg, borderRadius: 10, padding: 14, border: `1px solid ${theme.border}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
                <div>
                  <strong style={{ color: theme.navy, fontSize: 14 }}>{c.title || c.treatmentName || 'Consent'}</strong>
                  {c.consentKind && c.consentKind !== 'custom' && (
                    <span style={{ marginLeft: 6, padding: '2px 8px', background: '#f1f5f9', color: '#475569', borderRadius: 12, fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                      {c.consentKind === 'general' ? 'General' : c.consentKind === 'local-anaesthesia' ? 'LA' : c.consentKind === 'extraction' ? 'Extraction' : c.consentKind}
                    </span>
                  )}
                  {c.toothNumber && <span style={{ marginLeft: 6, padding: '2px 8px', background: '#e0f2fe', color: '#0369a1', borderRadius: 12, fontSize: 11, fontWeight: 600 }}>Tooth #{c.toothNumber}</span>}
                  {c.meta?.amount > 0 && <span style={{ marginLeft: 6, padding: '2px 8px', background: '#fef3c7', color: '#92400e', borderRadius: 12, fontSize: 11, fontWeight: 700 }}>₹{Number(c.meta.amount).toLocaleString('en-IN')}</span>}
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span style={{ fontSize: 12, color: theme.textMuted }}>{new Date(c.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                  <button onClick={() => {
                    const cw = window.open('', '_blank', 'width=800,height=900');
                    if (c.consentHtml) {
                      // New template — ready-to-print standalone HTML. Just paste it.
                      cw.document.write(c.consentHtml);
                      cw.document.close();
                      setTimeout(() => { cw.focus(); cw.print(); }, 400);
                    } else {
                      // Legacy plain-text consent — wrap in minimal shell.
                      const cText = (c.consentText || '').replace(/\n/g, '<br/>');
                      const cDate = new Date(c.date).toLocaleDateString('en-IN');
                      const tInfo = c.treatmentName ? `<div style="margin-bottom:15px;font-size:13px;"><strong>Treatment:</strong> ${c.treatmentName}${c.toothNumber ? ' (Tooth #' + c.toothNumber + ')' : ''}</div>` : '';
                      cw.document.write(`<!DOCTYPE html><html><head><title>Consent</title><style>*{margin:0;padding:0;box-sizing:border-box;}body{font-family:'Noto Sans Devanagari','Segoe UI',Arial,sans-serif;padding:30px;color:#000;font-size:14px;}.header{text-align:center;margin-bottom:20px;border-bottom:2px solid #000;padding-bottom:12px;}.consent-body{white-space:pre-wrap;line-height:1.8;font-size:13px;}.sign-line{border-top:1px solid #000;width:170px;margin-top:45px;padding-top:4px;text-align:center;font-size:11px;}@media print{body{padding:15px;}@page{size:A4;margin:15mm;}.no-print{display:none!important;}}</style></head><body><div class="header"><h2>${(clinic?.clinicName || 'Your Clinic').toUpperCase()}</h2><p style="font-size:11px;color:#555;">${[clinic?.clinicAddress, clinic?.clinicPhone].filter(Boolean).join(' | ')}</p><h3 style="margin-top:10px;">CONSENT FORM / सहमति पत्र</h3></div><div style="margin-bottom:15px;display:flex;justify-content:space-between;font-size:13px;"><span><strong>Patient:</strong> ${c.patientName || selectedPatient.name}</span><span><strong>UHID:</strong> ${selectedPatient.uhid}</span><span><strong>Date:</strong> ${cDate}</span></div>${tInfo}<hr style="margin-bottom:15px;"/><div class="consent-body">${cText}</div><div style="display:flex;justify-content:space-between;margin-top:40px;"><div class="sign-line">Doctor Signature</div><div class="sign-line">Patient Signature</div></div></body></html>`);
                      cw.document.close();
                      setTimeout(() => { cw.focus(); cw.print(); }, 400);
                    }
                  }} style={{ padding: '5px 12px', background: theme.primary, color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <Printer size={12}/> Print
                  </button>
                </div>
              </div>
              <div style={{ background: 'white', borderRadius: 8, padding: 10, fontSize: 12.5, color: '#374151', lineHeight: 1.5, whiteSpace: 'pre-wrap', maxHeight: 100, overflow: 'auto', border: `1px solid ${theme.borderLight}` }}>
                {c.consentHtml
                  ? (c.title || c.treatmentName) + ' — auto-generated NABH-compliant consent. Click Print to open the full form.'
                  : c.consentText}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  </div>
);

/* ─── EDIT MODAL (preserved from original, restyled) ─── */

const EditModal = ({ editForm, setEditForm, saveEdit, close, selectedPatient, medicalList, dentalList, allergiesList, groupsList, tagsList }) => (
  <div style={{ position: 'fixed', inset: 0, background: 'rgba(10,15,30,0.55)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} onClick={close}>
    <div onClick={e => e.stopPropagation()}
      style={{ background: 'white', borderRadius: 16, padding: 28, width: 760, maxWidth: '95vw', maxHeight: '88vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0, color: theme.navy, fontSize: 20 }}>Edit patient</h2>
        <button onClick={close} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: theme.textSubtle }}><X size={20}/></button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 16 }}>
        <div>
          <label style={labelStyle}>Title</label>
          <select value={editForm.title || ''} onChange={e => setEditForm({ ...editForm, title: e.target.value })} style={inputStyle}>
            <option value="">—</option><option>Mr.</option><option>Mrs.</option><option>Ms.</option><option>Master</option><option>Baby</option>
          </select>
        </div>
        <div><label style={labelStyle}>Name *</label><input value={editForm.name || ''} onChange={e => setEditForm({ ...editForm, name: e.target.value })} style={inputStyle}/></div>
        <div><label style={labelStyle}>Phone *</label><input value={editForm.phone || ''} onChange={e => setEditForm({ ...editForm, phone: e.target.value })} style={inputStyle}/></div>
        <div><label style={labelStyle}>Gender</label>
          <select value={editForm.gender || ''} onChange={e => setEditForm({ ...editForm, gender: e.target.value })} style={inputStyle}>
            <option value="">—</option><option>Male</option><option>Female</option><option>Other</option>
          </select>
        </div>
        <div><label style={labelStyle}>DOB</label>
          <input type="date" value={editForm.dob || ''} onChange={e => {
            const dob = e.target.value;
            const age = dob ? Math.floor((new Date() - new Date(dob)) / (365.25 * 24 * 60 * 60 * 1000)) : editForm.age;
            setEditForm({ ...editForm, dob, age });
          }} style={inputStyle}/>
        </div>
        <div><label style={labelStyle}>Age</label><input type="number" value={editForm.age || ''} onChange={e => setEditForm({ ...editForm, age: e.target.value })} style={inputStyle}/></div>
        <div style={{ gridColumn: 'span 2' }}><label style={labelStyle}>Address</label><input value={editForm.address || ''} onChange={e => setEditForm({ ...editForm, address: e.target.value })} style={inputStyle}/></div>
        <div><label style={labelStyle}>Occupation</label><input value={editForm.occupation || ''} onChange={e => setEditForm({ ...editForm, occupation: e.target.value })} style={inputStyle}/></div>
        <div><label style={labelStyle}>Referred by</label><input value={editForm.referredBy || ''} onChange={e => setEditForm({ ...editForm, referredBy: e.target.value })} style={inputStyle}/></div>
        <div><label style={labelStyle}>Aadhaar</label><input value={editForm.aadhaar || ''} onChange={e => setEditForm({ ...editForm, aadhaar: e.target.value })} style={inputStyle}/></div>
        <div><label style={labelStyle}>Insurance co.</label><input value={editForm.insurance?.companyName || ''} onChange={e => setEditForm({ ...editForm, insurance: { ...editForm.insurance, companyName: e.target.value } })} style={inputStyle}/></div>
        <div><label style={labelStyle}>Insurance type</label><input value={editForm.insurance?.subType || ''} onChange={e => setEditForm({ ...editForm, insurance: { ...editForm.insurance, subType: e.target.value } })} style={inputStyle}/></div>
      </div>

      <div style={{ padding: 14, background: theme.primaryLight, borderRadius: 10, marginBottom: 16, border: `1px solid ${theme.primary}22` }}>
        <label style={{ ...labelStyle, color: theme.primaryDark, marginBottom: 10, display: 'block' }}>Family linking</label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, alignItems: 'end' }}>
          <div><label style={labelStyle}>Family ID</label>
            <div style={{ display: 'flex', gap: 6 }}>
              <input value={editForm.familyId || ''} onChange={e => setEditForm({ ...editForm, familyId: e.target.value })} placeholder="FAM-1001" style={{ ...inputStyle, flex: 1 }}/>
              <button onClick={() => setEditForm({ ...editForm, familyId: `FAM-${selectedPatient.uhid || Date.now()}` })}
                style={{ padding: '8px 14px', background: theme.primary, color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 12 }}>Auto</button>
            </div>
          </div>
          <div><label style={labelStyle}>Relation</label>
            <select value={editForm.familyRelation || ''} onChange={e => setEditForm({ ...editForm, familyRelation: e.target.value })} style={inputStyle}>
              <option value="">Select…</option><option>Self</option><option>Spouse</option><option>Child</option><option>Parent</option><option>Sibling</option><option>Other</option>
            </select>
          </div>
        </div>
      </div>

      {[
        { label: 'Medical History', field: 'medicalHistory', list: medicalList || [],   color: theme.danger },
        { label: 'Dental History',  field: 'dentalHistory',  list: dentalList || [],    color: theme.warning },
        { label: 'Allergies',       field: 'allergies',      list: allergiesList || [], color: theme.pink },
      ].map(({ label, field, list, color }) => (
        <div key={field} style={{ marginBottom: 14 }}>
          <label style={labelStyle}>{label}</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {list.map(item => {
              const sel = (editForm[field] || []).includes(item.label);
              return (
                <span key={item._id} onClick={() => {
                  const updated = sel ? editForm[field].filter(x => x !== item.label) : [...(editForm[field] || []), item.label];
                  setEditForm({ ...editForm, [field]: updated });
                }} style={{ padding: '4px 12px', borderRadius: 16, cursor: 'pointer', fontSize: 12.5, fontWeight: 500, background: sel ? color : '#f0f0f0', color: sel ? 'white' : '#555', border: `1px solid ${sel ? color : '#ddd'}` }}>
                  {item.label}
                </span>
              );
            })}
          </div>
        </div>
      ))}

      <div style={{ marginBottom: 14 }}>
        <label style={labelStyle}>Groups</label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {(groupsList || []).map(g => {
            const sel = (editForm.groups || []).includes(g.label);
            return (
              <span key={g._id} onClick={() => {
                const updated = sel ? editForm.groups.filter(x => x !== g.label) : [...(editForm.groups || []), g.label];
                setEditForm({ ...editForm, groups: updated });
              }} style={{ padding: '4px 12px', borderRadius: 16, cursor: 'pointer', fontSize: 12.5, background: sel ? theme.navy : '#f0f0f0', color: sel ? 'white' : '#555' }}>
                {g.label}
              </span>
            );
          })}
        </div>
      </div>

      <div style={{ marginBottom: 14 }}>
        <label style={labelStyle}>Tags</label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {(tagsList || []).map(t => {
            const sel = (editForm.tags || []).includes(t.label);
            return (
              <span key={t._id} onClick={() => {
                const updated = sel ? editForm.tags.filter(x => x !== t.label) : [...(editForm.tags || []), t.label];
                setEditForm({ ...editForm, tags: updated });
              }} style={{ padding: '4px 12px', borderRadius: 16, cursor: 'pointer', fontSize: 12.5, background: sel ? theme.info : '#f0f0f0', color: sel ? 'white' : '#555' }}>
                #{t.label}
              </span>
            );
          })}
        </div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>Notes</label>
        <textarea value={editForm.notes || ''} onChange={e => setEditForm({ ...editForm, notes: e.target.value })} rows={2} style={{ ...inputStyle, resize: 'vertical' }}/>
      </div>

      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <button onClick={close} style={{ padding: '11px 24px', background: 'white', color: theme.navy, border: `1px solid ${theme.border}`, borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>Cancel</button>
        <button onClick={saveEdit} style={{ padding: '11px 24px', background: theme.primary, color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 700 }}>Save changes</button>
      </div>
    </div>
  </div>
);

/* ═══════════════════════ STYLES ═══════════════════════ */

const S = {
  root: {
    background: theme.bg,
    minHeight: '100%',
    // Negative margin pulls the panel out of the parent wrapper's 32px
    // padding so the top bar can sit flush with the app header. Right/bottom
    // remain negative to also escape side/bottom padding.
    margin: '-32px -32px -32px -32px',
    padding: 0,
  },
  topBar: {
    display: 'flex', alignItems: 'center', gap: 16,
    padding: '8px 24px',
    background: theme.surface,
    borderBottom: `1px solid ${theme.border}`,
    // Sticky removed — was causing card labels to clip when scrolled.
    flexWrap: 'wrap',
  },
  backBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '7px 14px', borderRadius: 8,
    background: 'white', border: `1px solid ${theme.border}`,
    color: theme.navy, fontWeight: 600, fontSize: 13, cursor: 'pointer',
  },
  tabBar: {
    display: 'flex', gap: 4, flex: 1, flexWrap: 'wrap',
    padding: 4, background: theme.bg, borderRadius: 10,
  },
  tabBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '7px 14px', border: 'none',
    background: 'transparent', color: theme.textMuted,
    borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
    transition: 'all 0.15s',
  },
  tabBtnActive: {
    background: theme.surface, color: theme.primaryDark,
    boxShadow: theme.shadowSm,
  },

  split: {
    display: 'grid',
    gridTemplateColumns: '320px 1fr',
    gap: 0,
    alignItems: 'flex-start',
    minHeight: 'calc(100vh - 140px)',
  },

  /* ── sidebar ── */
  sidebar: {
    background: theme.surface,
    borderRight: `1px solid ${theme.border}`,
    position: 'sticky', top: 72, alignSelf: 'flex-start',
    maxHeight: 'calc(100vh - 72px)',
    overflowY: 'auto',
    display: 'flex', flexDirection: 'column',
  },
  identityBlock: {
    padding: '24px 20px 20px',
    textAlign: 'center',
    borderBottom: `1px solid ${theme.borderLight}`,
  },
  photo: {
    width: 90, height: 90, borderRadius: '50%',
    objectFit: 'cover', border: `3px solid ${theme.primary}`,
    margin: '0 auto 12px', display: 'block',
    boxShadow: '0 6px 20px rgba(245,124,31,0.25)',
  },
  photoFallback: {
    width: 90, height: 90, borderRadius: '50%',
    background: `linear-gradient(135deg, ${theme.primary}, ${theme.secondary})`,
    color: 'white', fontWeight: 800, fontSize: 36,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    margin: '0 auto 12px',
    boxShadow: '0 6px 20px rgba(245,124,31,0.25)',
  },
  name: { fontSize: 18, fontWeight: 700, color: theme.navy, letterSpacing: '-0.01em' },
  uhid: { fontSize: 12, color: theme.textMuted, marginTop: 2, fontWeight: 600, letterSpacing: 0.5 },
  metaRow: { marginTop: 6, display: 'flex', justifyContent: 'center', gap: 6, fontSize: 13, color: theme.textMuted, fontWeight: 500 },

  dueBadge: {
    margin: '16px 16px 0',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '12px 14px', borderRadius: 10,
    background: '#fef2f2', border: '1px solid #fecaca',
    color: '#991b1b', cursor: 'pointer',
    textAlign: 'left', fontFamily: 'inherit',
  },
  dueLabel: { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: '#991b1b' },
  dueAmount: { fontSize: 18, fontWeight: 800, color: '#dc2626', marginTop: 2 },
  paidBadge: {
    margin: '16px 16px 0', padding: '10px 14px', borderRadius: 10,
    background: '#f0fdf4', border: '1px solid #bbf7d0',
    color: '#16a34a', fontSize: 13, fontWeight: 700, textAlign: 'center',
  },

  safetyBlock: {
    margin: '16px 16px 0',
    padding: 12,
    background: '#fff7ed',
    border: '1px solid #fed7aa',
    borderRadius: 10,
  },
  safetyHead: {
    display: 'flex', alignItems: 'center', gap: 6,
    fontSize: 11, fontWeight: 700, color: '#9a3412',
    textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8,
  },

  chipRow: { display: 'flex', flexWrap: 'wrap', gap: 4 },
  chipAllergy: {
    padding: '3px 10px', borderRadius: 12, fontSize: 11.5, fontWeight: 600,
    background: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca',
  },
  chipMedical: {
    padding: '3px 10px', borderRadius: 12, fontSize: 11.5, fontWeight: 600,
    background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a',
  },
  chipNeutral: {
    padding: '3px 10px', borderRadius: 12, fontSize: 11.5, fontWeight: 600,
    background: theme.secondaryLight, color: theme.secondaryDark, border: `1px solid ${theme.secondary}30`,
  },
  chipFamily: {
    padding: '3px 10px', borderRadius: 12, fontSize: 11.5, fontWeight: 700,
    background: '#eef2ff', color: '#3730a3', border: '1px solid #c7d2fe',
  },
  chipFamilyRel: {
    padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600,
    background: '#f1f5f9', color: theme.textMuted,
  },
  chipGroup: {
    padding: '3px 10px', borderRadius: 12, fontSize: 11.5, fontWeight: 600,
    background: theme.navy, color: 'white',
  },
  chipTag: {
    padding: '3px 10px', borderRadius: 12, fontSize: 11.5, fontWeight: 600,
    background: '#e0f2fe', color: '#0369a1',
  },

  sideRow: {
    display: 'flex', alignItems: 'flex-start', gap: 8,
    fontSize: 13, color: theme.navy, marginBottom: 6,
  },
  sideLink: { color: theme.primaryDark, textDecoration: 'none', fontWeight: 600 },

  /* Payments slide-in drawer */
  drawerBackdrop: {
    position: 'fixed', inset: 0, zIndex: 2500,
    background: 'rgba(10,15,30,0.45)',
    backdropFilter: 'blur(2px)',
    display: 'flex', justifyContent: 'flex-end',
  },
  paymentDrawer: {
    width: 560, maxWidth: '95vw', height: '100vh',
    background: theme.surface,
    boxShadow: '-20px 0 60px rgba(15,23,42,0.25)',
    display: 'flex', flexDirection: 'column',
    animation: 'ozSlideInRight 0.3s cubic-bezier(.2,.8,.2,1)',
  },
  drawerHead: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '18px 22px',
    borderBottom: `1px solid ${theme.borderLight}`,
  },
  drawerTitle: {
    fontSize: 18, fontWeight: 700, color: theme.navy,
    letterSpacing: '-0.01em',
  },
  drawerSub: { fontSize: 12.5, color: theme.textMuted, marginTop: 2 },
  drawerClose: {
    width: 32, height: 32, borderRadius: 8,
    background: theme.bg, border: `1px solid ${theme.border}`,
    color: theme.navy, cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },

  /* ── content ── */
  content: {
    padding: '12px 24px 24px 24px',
    display: 'flex', flexDirection: 'column',
    gap: 12,
  },
  paneCard: {
    background: theme.surface,
    borderRadius: 14,
    border: `1px solid ${theme.border}`,
    boxShadow: theme.shadowSm,
    padding: 20,
  },

  kpiGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(5, 1fr)',
    gap: 12,
  },
  kpiCard: {
    background: theme.surface,
    border: `1px solid ${theme.border}`,
    borderRadius: 12, padding: '14px 16px',
    boxShadow: theme.shadowSm,
  },
  kpiLabel: {
    fontSize: 11, fontWeight: 700, color: theme.textMuted,
    textTransform: 'uppercase', letterSpacing: 0.8,
  },
  kpiValue: {
    fontSize: 20, fontWeight: 800, letterSpacing: '-0.01em',
  },

  planRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '10px 12px', borderRadius: 8,
    background: '#fffbeb', border: '1px solid #fef3c7',
  },

  miniCard: {
    padding: '10px 12px', borderRadius: 8,
    background: theme.bg, border: `1px solid ${theme.border}`,
  },

  primaryBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '8px 16px', borderRadius: 8,
    background: theme.primary, color: 'white', border: 'none',
    fontSize: 13, fontWeight: 600, cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(245,124,31,0.3)',
  },
  ghostBtn: {
    padding: '6px 12px', borderRadius: 8,
    background: 'transparent', color: theme.primaryDark,
    border: 'none', fontSize: 12, fontWeight: 600, cursor: 'pointer',
  },

  /* ── modals ── */
  modalBackdrop: {
    position: 'fixed', inset: 0, background: 'rgba(10,15,30,0.55)',
    zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  modalCard: {
    background: 'white', borderRadius: 16, padding: 28,
    width: 440, maxWidth: '90vw',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
    textAlign: 'center',
  },
  printModeBtnA: {
    flex: 1, padding: 20, borderRadius: 12,
    border: `2px solid ${theme.primary}`,
    background: theme.primaryLight, color: theme.primaryDark,
    fontWeight: 700, fontSize: 14, cursor: 'pointer',
  },
  printModeBtnB: {
    flex: 1, padding: 20, borderRadius: 12,
    border: `2px solid ${theme.border}`,
    background: 'white', color: theme.navy,
    fontWeight: 700, fontSize: 14, cursor: 'pointer',
  },
};

export default PatientProfile;
